/**
 * PoCreateFromBomPage.tsx
 *
 * Giai đoạn 6 — Multi-Product PO từ BOM.
 * Conforms to Section F of docs/superpowers/plans/2026-10-02-bom-explosion-canonical-spec.md
 */
import { useEffect, useMemo, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Layers,
  Plus,
  Trash2,
} from 'lucide-react'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { WorkspacePage } from '@/components/layout/WorkspacePage'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { DateInput } from '@/components/forms/DateInput'
import { ProductSearchPicker } from './ProductSearchPicker'
import { buildFoamChoices, findFoamChoice, foamChoiceKey, pickAutoFoamChoice, type FoamChoice } from './foamConfigChoice'
import { getProductFoamBomConfigsApi } from '@/services/boms/productFoamBomApiClient'
import { formatDateFull } from '@/lib/dateFormat'
import {
  ExcelEditableTable,
  type ExcelColumn,
} from '@/components/table/ExcelEditableTable'
import { formatVnNumber } from '@/features/purchasing/poWorkflow'
import { supplierService, type Supplier } from '@/services/suppliers'
import { productService, type Product } from '@/services/products'
import { resolvePriceBatchApi } from '@/services/procurement/apiClient'
import type { ResolvePricePayload } from '@/services/procurement/types'
import {
  createPurchaseOrder,
  type CreatePoLinePayload,
  ProcurementApiClientError,
} from '@/services/procurement'
import { serializeSpecKey } from './specKey'
import {
  resolveBomAdapterApi,
  createMaterialRequirementBatchApi,
  type CanonicalBomSource,
  type StandardAssemblyType,
  type ExplosionInputLine,
  type ExplosionResultLine,
  type CreateBatchLineInput,
  type CreateBatchContributionInput,
  type MaterialRequirementBatchRow,
} from '@/services/procurement/materialRequirementApiClient'
import { getProductBomConfigsApi } from '@/services/boms/productBomSpreadsheetApiClient'
import type { BomConfigRow } from '@/features/boms/types/bomSpreadsheetTypes'
import { apiFetch } from '@/services/apiFetch'
import { PoRulePickerModal, type ActiveRuleOption } from './PoRulePickerModal'
import { assignSupplier, bomSheetValues, groupBomPoLines, isSameBomBatchRun, matchSheetRules, requireStandardFoamQuoteMatch, type SheetRuleForBomMatch } from './poFromBomMultiSupplier'
import { assemblyTypeLabel, istaLevelLabel } from '@/lib/productConfigLabels'

export interface ProductConfigOptionsState {
  loading: boolean
  error: string | null
  colors: string[]
  woods: string[]
}

export function extractProductBomColorAndWoodOptions(configs: BomConfigRow[]): {
  colors: string[]
  woods: string[]
} {
  const okConfigs = configs.filter((c) => c.status === 'OK')
  const targetConfigs = okConfigs.length > 0 ? okConfigs : configs

  const seenColors = new Set<string>()
  const colors: string[] = []
  for (const cfg of targetConfigs) {
    for (const c of cfg.colors || []) {
      const trimmed = typeof c === 'string' ? c.trim() : ''
      if (trimmed) {
        const lower = trimmed.toLowerCase()
        if (!seenColors.has(lower)) {
          seenColors.add(lower)
          colors.push(trimmed)
        }
      }
    }
  }

  const seenWoods = new Set<string>()
  const woods: string[] = []
  for (const cfg of targetConfigs) {
    for (const w of cfg.woods || []) {
      const trimmed = typeof w === 'string' ? w.trim() : ''
      if (trimmed) {
        const lower = trimmed.toLowerCase()
        if (!seenWoods.has(lower)) {
          seenWoods.add(lower)
          woods.push(trimmed)
        }
      }
    }
  }

  return { colors, woods }
}

export interface SelectedProductRow {
  id: string
  product_id: string
  product_code: string
  product_name: string
  order_qty: number
  /** '' = chưa chọn (Mút xốp: chờ chọn cấu hình BOM khi mã có từ 2 cấu hình trở lên). */
  assembly_type: StandardAssemblyType | ''
  color?: string
  wood_type?: string
  packaging_type?: 'thuong' | 'ecom'
  override_wood_config_id?: string
  override_foam_config_id?: string
}

export interface BomPreviewTableRow extends Record<string, unknown> {
  id: string
  product_id: string
  product_code: string
  material_id: string | null
  material_code: string | null
  material_name: string
  bom_material_name?: string
  /** Chuỗi quy cách đầy đủ (kích thước - định lượng): dùng để khớp giá/tạo PO, KHÔNG hiển thị trực tiếp. */
  spec: string
  /** Thuộc tính của cấu hình BOM đã chọn: hiển thị ở cột Kết cấu / Đóng gói. */
  bom_assembly?: string
  bom_packaging?: string
  /** Cột "Quy cách" hiển thị: chỉ kích thước (ví dụ 300x80x40, 90x90x90x20). */
  dimensions?: string
  /** Định lượng từ trường BOM chuẩn; thùng và góc giấy để trống. */
  density?: string
  /** Màu (ngũ kim): lấy từ định mức, cần để mua đúng. */
  color?: string
  material_spec?: Record<string, unknown> | null
  material_spec_raw?: string | null
  uom: string
  qty_gross: number
  qty_actual_buy: number
  unit_price: number | null
  line_total: number
  selected_rule_id: string | null
  price_source: 'auto' | 'manual' | 'none'
  manual_reason: string
  price_warning: string | null
  resolution_status: 'resolved' | 'unresolved'
  manual_resolution?: boolean
  resolution_notes: string
  notes: string
  source_bom_line_id: string
  bom_source: CanonicalBomSource
  quote_status?: string
  candidates?: Array<{
    rule_id: string
    label: string
    unit_price: number
    uom: string
    score: number
    specificity: number
  }>
  supplier_id?: string | null
  supplier_name?: string | null
  supplier_candidates?: Array<{ id: string; name: string }>
  supplier_requires_confirmation?: boolean
  sheet_inputs?: Record<string, number>
  sheet_input_names?: string[]
  rule_label?: string
  purchase_inputs?: Record<string, number | string>
}

/** Only catalog-resolved BOM rows can be sent to a supplier PO. */
export function rowsEligibleForBomPurchaseOrders(rows: BomPreviewTableRow[]): BomPreviewTableRow[] {
  return rows.filter((row) => row.resolution_status === 'resolved' && Boolean(row.material_id) && Boolean(row.supplier_id))
}

export function countPricedResolvedRows(rows: BomPreviewTableRow[]): number {
  return rows.filter((row) => row.resolution_status === 'resolved'
    && typeof row.unit_price === 'number' && Number.isFinite(row.unit_price) && row.unit_price > 0).length
}

export function parseProductOrderQty(value: string): number {
  const normalized = value.trim()
  if (!normalized) return 0
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : 0
}

export function applySelectedRuleToUnresolvedRow(
  row: BomPreviewTableRow,
  rule: ActiveRuleOption,
): BomPreviewTableRow {
  const attributes = rule.item_attributes ?? {}
  const color = String(attributes.mau ?? attributes.color ?? '').trim()
  const reason = row.resolution_notes?.trim() ?? ''
  const notes = [row.notes?.trim(), reason].filter((note, index, values) => note && values.indexOf(note) === index).join('; ')
  return {
    ...row,
    material_id: rule.item_id,
    material_code: rule.item_code ?? row.material_code,
    material_name: rule.item_name ?? row.material_name,
    color: color || row.color,
    material_spec: color ? { ...(row.material_spec ?? {}), color } : row.material_spec,
    uom: 'Pcs',
    supplier_id: rule.supplier_id ?? row.supplier_id,
    supplier_name: rule.supplier_name ?? row.supplier_name,
    supplier_requires_confirmation: false,
    selected_rule_id: rule.id,
    manual_resolution: true,
    rule_label: rule.supplier_item_name,
    notes,
    resolution_status: 'resolved',
    unit_price: null,
    line_total: 0,
    price_source: 'none',
    quote_status: 'RESOLVING',
    price_warning: null,
  }
}

export interface ResolveBomSignatureInput {
  supplierId: string | null
  deliveryDate: string
  bomSource: CanonicalBomSource
  products: SelectedProductRow[]
}

export function buildResolveBomSignature(input: ResolveBomSignatureInput): string {
  const prodsPart = (input.products || [])
    .map(
      (p) =>
        `${p.product_id}:${p.product_code}:${p.order_qty}:${p.assembly_type}:${p.packaging_type || ''}:${p.color || ''}:${p.wood_type || ''}:${p.override_foam_config_id || ''}:${p.override_wood_config_id || ''}`,
    )
    .join(';')
  return [
    input.supplierId || '',
    input.deliveryDate || '',
    input.bomSource || '',
    prodsPart,
  ].join('#')
}

export interface BomRowPricingSignatureInput {
  supplierId: string | null
  materialId: string | null
  spec?: string | null
  materialSpec?: Record<string, unknown> | null
  orderUom?: string | null
  qty: number
  asOf: string
  selectedRuleId?: string | null
}

export function buildBomRowPricingSignature(input: BomRowPricingSignatureInput): string {
  const specKey = serializeSpecKey(input.materialSpec)
  return [
    input.supplierId || '',
    input.materialId || '',
    specKey,
    (input.spec || '').trim(),
    (input.orderUom || '').trim().toLowerCase(),
    input.qty,
    input.asOf || '',
    input.selectedRuleId || '',
  ].join('|')
}

export interface StructuredSpecPricingFields {
  thick: number | null
  width: number | null
  length: number | null
  height: number | null
  kt1: number | string | null
  kt2: number | string | null
  kt3: number | string | null
  density: number | string | null
  packagingKind: string | null
  quality: string | null
}

/**
 * Single authoritative mapper from material_spec to ResolvePricePayload structured fields (Blocker 1).
 */
export function mapMaterialSpecToPricingFields(
  materialSpec?: Record<string, unknown> | null,
): StructuredSpecPricingFields {
  if (!materialSpec || typeof materialSpec !== 'object') {
    return {
      thick: null,
      width: null,
      length: null,
      height: null,
      kt1: null,
      kt2: null,
      kt3: null,
      density: null,
      packagingKind: null,
      quality: null,
    }
  }

  const spec = materialSpec as Record<string, unknown>

  const thick =
    typeof spec.thickness === 'number'
      ? spec.thickness
      : typeof spec.thick === 'number'
        ? spec.thick
        : null

  const width = typeof spec.width === 'number' ? spec.width : null
  const length = typeof spec.length === 'number' ? spec.length : null
  const height = typeof spec.height === 'number' ? spec.height : null

  const kt1 = (spec.kt1 as any) ?? thick
  const kt2 = (spec.kt2 as any) ?? width
  const kt3 = (spec.kt3 as any) ?? length

  const density = (spec.density as any) ?? null

  const packagingKind =
    typeof spec.packaging_kind === 'string'
      ? spec.packaging_kind
      : typeof spec.packagingKind === 'string'
        ? spec.packagingKind
        : null

  const quality =
    typeof spec.quality === 'string'
      ? spec.quality
      : typeof spec.quality_code === 'string'
        ? spec.quality_code
        : typeof spec.quality_grade === 'string'
          ? spec.quality_grade
          : null

  return {
    thick,
    width,
    length,
    height,
    kt1,
    kt2,
    kt3,
    density,
    packagingKind,
    quality,
  }
}

/**
 * Builds a uniform ResolvePricePayload from a BomPreviewTableRow (Blocker 1).
 */
export function buildResolvePricePayloadFromRow(
  row: BomPreviewTableRow,
  supplierId: string,
  asOf: string,
  overrides: Partial<ResolvePricePayload> = {},
): ResolvePricePayload {
  const specFields = mapMaterialSpecToPricingFields(row.material_spec)
  const qty = Number(row.qty_actual_buy ?? row.qty_gross)

  return {
    supplierId,
    materialId: row.material_id || null,
    spec: row.spec || null,
    ...specFields,
    uom: row.uom || null,
    orderUom: row.uom || null,
    qty: isNaN(qty) ? null : qty,
    asOf,
    selectedRuleId: row.selected_rule_id || null,
    allowEstimate: false,
    ...overrides,
  }
}



/**
 * Pure helper: Validate product inputs before calling BOM Adapter.
 */
export function validateProductInputs(
  supplierId: string | null,
  products: SelectedProductRow[],
  bomSource: CanonicalBomSource,
): string | null {
  void supplierId
  if (!products || products.length === 0) {
    return 'Vui lòng chọn ít nhất 1 mã hàng'
  }
  for (let i = 0; i < products.length; i++) {
    const p = products[i]
    const idx = i + 1
    if (!p.product_id || !p.product_code) {
      return `Dòng ${idx}: Thiếu thông tin mã hàng`
    }
    if (typeof p.order_qty !== 'number' || isNaN(p.order_qty) || p.order_qty <= 0) {
      return `Dòng ${idx} (${p.product_code}): Số lượng đặt phải là số dương lớn hơn 0`
    }
    if (bomSource === 'foam' && !p.assembly_type) {
      return `Dòng ${idx} (${p.product_code}): Vui lòng chọn cấu hình BOM (kết cấu - đóng gói) của mã hàng`
    }
    if (p.assembly_type !== 'assembled' && p.assembly_type !== 'kd') {
      return `Dòng ${idx} (${p.product_code}): Kết cấu (assembly_type) bắt buộc chọn ${assemblyTypeLabel('kd')} hoặc ${assemblyTypeLabel('assembled')}`
    }
    if (bomSource === 'foam') {
      if (!p.packaging_type || (p.packaging_type !== 'thuong' && p.packaging_type !== 'ecom')) {
        return `Dòng ${idx} (${p.product_code}): Đóng gói bắt buộc chọn 'Thường' hoặc 'Ecom' đối với Mút xốp`
      }
    }
  }
  return null
}

/**
 * Pure helper: Build ExplosionInputLine[] payload from selected products.
 */
async function searchProductsForPicker(query: string, limit: number): Promise<Product[]> {
  const res = await productService.list({ search: query || undefined, pageSize: limit, sortBy: 'code', lite: true })
  return res.items.filter((p) => p.status === 'ok' || p.status === 'pending')
}

/**
 * Giữ mỗi dòng BOM gọn một hàng; nội dung dài được cắt trong ô để không làm thay đổi mật độ dòng.
 */
export const BOM_TABLE_AUTOFIT_CSS = `
.bom-autofit-table td > div,
.bom-autofit-table td > div.truncate {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  overflow-wrap: normal;
  padding: 0 4px !important;
  min-height: 0 !important;
  line-height: normal !important;
}
.bom-autofit-table th,
.bom-autofit-table th * {
  white-space: nowrap !important;
  overflow-wrap: normal !important;
  word-break: normal !important;
}
.bom-autofit-table th {
  position: sticky !important;
  top: 0;
  vertical-align: middle !important;
  line-height: 1.15 !important;
}
.bom-autofit-table td,
.bom-autofit-table th { padding-left: 0 !important; padding-right: 0 !important; }
.bom-autofit-table th > div,
.bom-autofit-table th > span { padding: 3px 4px !important; }
.bom-autofit-table table { width: max-content !important; min-width: 100% !important; }
.bom-workspace-header { display: flex; flex-wrap: wrap; align-items: center; column-gap: 12px; row-gap: 0; }
.bom-workspace-header nav,
.bom-workspace-header > div { margin: 0 !important; }
.bom-workspace-header > div { flex: 1 1 520px; }
.bom-workspace-page > section:first-of-type > div:first-child { min-height: 32px; padding-top: 0; padding-bottom: 0; }
.bom-workspace-page .bom-preview-heading { padding-top: 4px !important; padding-bottom: 0 !important; }
.bom-workspace-page .excel-table-settings-bar { padding-top: 0; padding-bottom: 0; }
.bom-workspace-page .excel-table-selection-footer { padding-top: 2px; padding-bottom: 2px; }
.bom-workspace-page .bom-workspace-footer { padding-top: 4px; padding-bottom: 4px; }
.bom-autofit-table th { padding-top: 0 !important; padding-bottom: 0 !important; }
@media (max-height: 800px) {
  .bom-workspace-page > section:nth-of-type(2) > section > div:first-child { padding-top: 2px; padding-bottom: 0; }
  .bom-workspace-page .bom-missing-price-warning { margin-bottom: 4px; padding-top: 4px; padding-bottom: 4px; }
  .bom-workspace-page .bom-workspace-footer { gap: 4px; }
}
.bom-autofit-table td input,
.bom-autofit-table td select { padding: 1px 4px !important; min-width: 0; min-height: 24px !important; }
.bom-autofit-table .excel-editable-table-container { height: 100% !important; min-height: 0; border: 0; border-radius: 0; box-shadow: none; }
`

export function getDefaultHiddenBomColumns(rows: BomPreviewTableRow[], _bomSource: CanonicalBomSource): string[] {
  const hidden: string[] = []
  if (rows.every((row) => !String(row.color ?? '').trim())) hidden.push('color')
  if (rows.every((row) => !String(row.density ?? '').trim())) hidden.push('density')
  if (rows.every((row) => !(row.sheet_input_names ?? []).length)) hidden.push('sheet_inputs')
  if (rows.every((row) => !String(row.notes ?? '').trim())) hidden.push('notes')
  return hidden
}

const BOM_SOURCE_LABEL: Record<string, string> = {
  foam: 'Mút xốp',
  hardware: 'Kim khí / Phụ kiện',
  wood: 'Gỗ',
}

/** Thông báo khi BOM bóc ra 0 dòng vật tư (trước đây trang im lặng, không hiện gì). */
export function buildEmptyBomMessage(
  products: Array<{ product_code: string }>,
  bomSource: string,
  partial = false,
): string {
  const codes = products.map((p) => p.product_code).join(', ')
  const label = BOM_SOURCE_LABEL[bomSource] ?? bomSource
  return partial
    ? `Mã hàng ${codes}: BOM ${label} không có dòng vật tư nào. Các mã còn lại đã được tính. Kiểm tra BOM của mã này (kết cấu, đóng gói) hoặc xóa mã khỏi danh sách.`
    : `BOM ${label} của mã hàng ${codes} đã được tìm thấy nhưng không có dòng vật tư nào để tính. Kiểm tra BOM của mã hàng (đúng kết cấu và hình thức đóng gói, có nhập dòng vật tư và định mức) hoặc chọn mã khác.`
}

export function productPickerLabel(p: Pick<Product, 'code' | 'name'>): string {
  return p.name ? `${p.code} — ${p.name}` : p.code
}

export function buildExplosionInputs(
  products: SelectedProductRow[],
  bomSource: CanonicalBomSource,
): ExplosionInputLine[] {
  return products.map((p) => ({
    product_id: p.product_id,
    product_code: p.product_code,
    order_qty: Number(p.order_qty),
    attributes: {
      assembly_type: p.assembly_type as StandardAssemblyType,
      color: p.color?.trim() || undefined,
      wood_type: p.wood_type?.trim() || undefined,
      packaging_type: bomSource === 'foam' ? p.packaging_type : undefined,
    },
    bom_sources: [bomSource],
    override_wood_config_id: p.override_wood_config_id || undefined,
    override_foam_config_id: p.override_foam_config_id || undefined,
  }))
}

/** Nhãn cột "Kết cấu" (từ thuộc tính BOM). */
export function bomAssemblyLabel(v: unknown): string {
  return v === 'kd' ? 'Tháo rời' : v === 'assembled' ? 'Ráp chết' : ''
}

/** Nhãn cột "Đóng gói" (từ thuộc tính BOM); BOM không phân loại đóng gói ('all') thì để trống. */
export function bomPackagingLabel(v: unknown): string {
  return v === 'thuong' ? 'Thường' : v === 'ecom' ? 'Ecom' : ''
}

/**
 * Tách quy cách hiển thị thành 2 phần: kích thước (cột "Quy cách") và định lượng (cột "Định lượng").
 * Kích thước chuỗi ghép như "90x90x20" được giữ nguyên (kt1/kt2/kt3), không bị ép thành số.
 */
export function splitSpecForDisplay(
  materialSpec: Record<string, unknown> | null | undefined,
  specRaw?: string | null,
): { dimensions: string; density: string } {
  const spec = materialSpec && typeof materialSpec === 'object' ? materialSpec : null
  const part = (raw: unknown, fallback: unknown): string => {
    const v = raw ?? fallback
    if (v === null || v === undefined) return ''
    return String(v).trim()
  }
  const typeCode = String(spec?.type_code ?? '')
  const isCarton = typeCode === 'thung'
  const dims = spec?.standard_foam_bom === true
    ? [part(spec.kt1, null), part(spec.kt2, null), part(spec.kt3, null), part(spec.kt4, null)].filter(Boolean).join('x')
    : spec
    ? (isCarton
      ? [part(spec.length, spec.kt1), part(spec.width, spec.kt2), part(spec.height, spec.kt3)]
      : [part(spec.kt1, spec.thickness), part(spec.kt2, spec.width), part(spec.kt3, spec.length)])
      .filter(Boolean).join('x')
    : ''
  const density = spec && typeCode !== 'thung' && typeCode !== 'goc_giay'
    ? String(spec.dinh_luong ?? spec.density ?? '').trim()
    : ''

  if (dims || density) {
    return { dimensions: dims, density }
  }
  // Không có quy cách có cấu trúc (ví dụ dòng chỉ có chuỗi thô): giữ chuỗi thô ở cột Quy cách.
  return { dimensions: (specRaw ?? '').trim(), density: '' }
}

/**
 * Pure helper: Map explosion results to flat BomPreviewTableRow[] (no merged cells).
 * Every contribution from a product is preserved as its own row.
 */
export function mapExplosionResultToPreviewRows(
  resultLines: ExplosionResultLine[],
): BomPreviewTableRow[] {
  const rows: BomPreviewTableRow[] = []
  for (const line of resultLines) {
    const specRaw =
      line.material_spec_raw ??
      (typeof line.material_spec === 'string' ? line.material_spec : null) ??
      (typeof (line as any).spec === 'string' ? (line as any).spec : null) ??
      ''
    const specObj =
      typeof line.material_spec === 'object' && line.material_spec !== null
        ? line.material_spec
        : (typeof (line as any).spec === 'object' && (line as any).spec !== null ? (line as any).spec : null)

    for (let cIdx = 0; cIdx < line.contributions.length; cIdx++) {
      const contrib = line.contributions[cIdx]
      const rowId = `prev-${line.material_id ?? 'unres'}-${contrib.product_code}-${contrib.source_bom_line_id}-${cIdx}`
      const display = splitSpecForDisplay(specObj, specRaw)
      rows.push({
        id: rowId,
        product_id: contrib.product_id,
        product_code: contrib.product_code,
        material_id: line.material_id,
        material_code: line.material_code,
        material_name: line.material_name,
        bom_material_name: line.material_name_raw ?? line.material_name,
        spec: specRaw,
        dimensions: display.dimensions,
        density: display.density,
        color: line.material_color || (specObj && specObj.color != null ? String(specObj.color).trim() : ''),
        bom_assembly: contrib.assembly_type ?? '',
        bom_packaging: contrib.packaging_option ?? '',
        material_spec: specObj,
        material_spec_raw: specRaw,
        uom: line.resolution_status === 'resolved' ? 'Pcs' : line.uom,
        qty_gross: contrib.qty,
        qty_actual_buy: contrib.qty,
        unit_price: null,
        line_total: 0,
        selected_rule_id: null,
        price_source: 'none',
        manual_reason: '',
        price_warning: line.material_id ? null : 'Vật tư chưa liên kết danh mục',
        quote_status: line.material_id ? undefined : 'MATERIAL_NOT_SELECTED',
        resolution_status: line.resolution_status,
        resolution_notes: line.resolution_notes ?? '',
        // Ghi chú = ghi chú của dòng BOM (người mua sửa được); BOM không ghi chú thì để trống, không điền chữ hệ thống.
        notes: contrib.note ?? '',
        source_bom_line_id: contrib.source_bom_line_id,
        bom_source: contrib.bom_source,
      })
    }
  }
  return rows
}

/**
 * Authoritative Server Pricing for PO-from-BOM preview rows.
 * Uses POST /api/pricing/resolve-batch with allowEstimate = false.
 */
export async function resolvePricesForBomPreviewRows(
  rows: BomPreviewTableRow[],
  supplierId: string,
  deliveryDate?: string,
): Promise<BomPreviewTableRow[]> {
  if (!supplierId || rows.length === 0) return rows

  const validIndices: number[] = []
  const items: ResolvePricePayload[] = []

  rows.forEach((r, idx) => {
    if (r.price_source === 'manual' && r.unit_price && r.unit_price > 0) {
      return
    }

    const qty = Number(r.qty_actual_buy ?? r.qty_gross)
    if (isNaN(qty) || qty <= 0) {
      return
    }

    validIndices.push(idx)
    items.push(
      buildResolvePricePayloadFromRow(
        r,
        supplierId,
        deliveryDate || new Date().toISOString().slice(0, 10),
        { qty },
      ),
    )
  })

  const results = items.length > 0 ? await resolvePriceBatchApi(items) : []
  const resultsByOriginalIndex = new Map<number, any>()
  validIndices.forEach((origIdx, i) => {
    resultsByOriginalIndex.set(origIdx, results[i])
  })

  return rows.map((row, idx) => {
    // Preserve manual prices if user explicitly entered them
    if (row.price_source === 'manual' && row.unit_price && row.unit_price > 0) {
      return row
    }

    const qty = Number(row.qty_actual_buy ?? row.qty_gross)
    if (isNaN(qty) || qty <= 0) {
      return {
        ...row,
        unit_price: null,
        line_total: 0,
        selected_rule_id: null,
        price_source: 'none',
        manual_reason: '',
        price_warning: 'Số lượng mua thực tế phải lớn hơn 0',
        quote_status: 'INVALID_QTY',
        candidates: undefined,
      }
    }

    const res = resultsByOriginalIndex.get(idx)
    if (!res) return row

    if (res.status === 'MATCHED' && res.unitPrice != null && res.lineTotal != null) {
      return {
        ...row,
        unit_price: res.unitPrice,
        line_total: res.lineTotal,
        selected_rule_id: res.ruleId || res.priceRuleId || null,
        price_source: 'auto',
        manual_reason: '',
        price_warning: null,
        quote_status: 'MATCHED',
        candidates: undefined,
      }
    }

    if (res.status === 'MATCHED' && res.lineTotal == null) {
      return {
        ...row,
        unit_price: null,
        line_total: 0,
        selected_rule_id: null,
        price_source: 'none',
        manual_reason: '',
        price_warning: 'Lỗi máy chủ: Thiếu thông tin thành tiền (lineTotal)',
        quote_status: 'ERROR',
        candidates: undefined,
      }
    }

    if (res.status === 'AMBIGUOUS') {
      return {
        ...row,
        unit_price: null,
        line_total: 0,
        selected_rule_id: null,
        price_source: 'none',
        manual_reason: '',
        price_warning: res.reason || 'Trùng giá — có nhiều dòng báo giá phù hợp',
        quote_status: 'AMBIGUOUS',
        candidates: res.candidates,
      }
    }

    let warning = res.reason || 'Chưa có báo giá phù hợp'
    if (res.status === 'QUOTE_ITEMS_NOT_MAPPED') {
      warning = 'Vật tư chưa được map vào báo giá NCC'
    } else if (res.status === 'NO_QUOTE') {
      warning = 'NCC chưa có báo giá cho vật tư này'
    } else if (res.status === 'MATERIAL_NOT_SELECTED') {
      warning = 'Vật tư chưa liên kết danh mục'
    }

    return {
      ...row,
      unit_price: null,
      line_total: 0,
      selected_rule_id: null,
      price_source: 'none',
      manual_reason: '',
      price_warning: warning,
      quote_status: res.status,
      candidates: undefined,
    }
  })
}

/**
 * Pure helper: Merge batch pricing results into current preview rows.
 * Conforms to Invariant:
 * 1. Preserves manual prices if user explicitly entered them (never overwritten by auto batch).
 * 2. Preserves fresh user state (e.g. qty, note, candidate) if row sequence or signature was bumped while batch was in flight.
 * 3. Applies server authoritative pricing (unitPrice and lineTotal).
 */
export function mergeBomPricingResults(
  currentRows: BomPreviewTableRow[],
  pricedResults: BomPreviewTableRow[],
  dispatchedSeqMap: Map<string, number>,
  dispatchedSigMap: Map<string, string>,
  currentRowSeqMap: Map<string, number>,
  currentRowSigMap: Map<string, string>,
): BomPreviewTableRow[] {
  const resultMap = new Map<string, BomPreviewTableRow>()
  pricedResults.forEach((pr) => resultMap.set(pr.id, pr))

  return currentRows.map((cur) => {
    // 1. If user entered manual price in between, keep it!
    if (cur.price_source === 'manual') return cur

    // 2. If row sequence or signature changed (user edited qty or picked candidate in between), keep it!
    const dispatchedSeq = dispatchedSeqMap.get(cur.id)
    if (dispatchedSeq !== undefined && currentRowSeqMap.get(cur.id) !== dispatchedSeq) {
      return cur
    }
    const dispatchedSig = dispatchedSigMap.get(cur.id)
    if (dispatchedSig !== undefined && currentRowSigMap.get(cur.id) !== dispatchedSig) {
      return cur
    }

    const pr = resultMap.get(cur.id)
    if (!pr) return cur

    return {
      ...cur,
      unit_price: pr.unit_price,
      line_total: pr.line_total,
      selected_rule_id: pr.selected_rule_id,
      price_source: pr.price_source,
      manual_reason: pr.manual_reason,
      price_warning: pr.price_warning,
      quote_status: pr.quote_status,
      candidates: pr.candidates,
    }
  })
}

/**
 * Pure helper: Validate prices of all preview rows before creating PO.
 */
export function validatePriceForPreviewRows(rows: BomPreviewTableRow[]): string | null {
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    const idx = i + 1
    if (r.resolution_status === 'unresolved' || !r.material_id) {
      return `Dòng ${idx} (${r.product_code} — ${r.material_name}): Vật tư chưa được phân giải. Hãy chọn vật tư hoặc bỏ dòng trước khi tạo PO.`
    }
    if (r.price_source === 'manual' && (!r.manual_reason || !r.manual_reason.trim())) {
      return `Dòng ${idx} (${r.product_code} — ${r.material_name}): Giá nhập tay bắt buộc có lý do (manual_reason).`
    }
  }
  return null
}

/**
 * Pure helper: Aggregate preview rows by material_id + uom + canonical spec to CreateBatchLineInput[].
 */
export function aggregatePreviewRowsToBatchLines(
  rows: BomPreviewTableRow[],
): CreateBatchLineInput[] {
  const bucketMap = new Map<string, CreateBatchLineInput>()

  for (const row of rows) {
    const specKey = serializeSpecKey(row.material_spec)
    const normUom = (row.uom || '').trim().toLowerCase()
    const key = `${row.material_id ?? `unres-${row.material_name}`}::${normUom}::${specKey}`
    const existing = bucketMap.get(key)
    const contrib: CreateBatchContributionInput = {
      product_id: row.product_id,
      product_code: row.product_code,
      qty: Number(row.qty_actual_buy),
      source_bom_line_id: row.source_bom_line_id,
      bom_source: row.bom_source,
    }

    if (existing) {
      existing.qty_gross += Number(row.qty_gross)
      existing.qty_actual_buy = (existing.qty_actual_buy ?? 0) + Number(row.qty_actual_buy)
      existing.contributions.push(contrib)
      if (row.notes && !existing.notes?.includes(row.notes)) {
        existing.notes = existing.notes ? `${existing.notes}; ${row.notes}` : row.notes
      }
      if (!existing.material_spec && row.material_spec) {
        existing.material_spec = row.material_spec
      }
      if (!existing.material_spec_raw && (row.material_spec_raw || row.spec)) {
        existing.material_spec_raw = row.material_spec_raw || row.spec
      }
    } else {
      bucketMap.set(key, {
        material_id: row.material_id,
        material_code: row.material_code,
        material_name: row.material_name,
        uom: row.uom,
        qty_gross: Number(row.qty_gross),
        qty_actual_buy: Number(row.qty_actual_buy),
        resolution_status: row.resolution_status,
        notes: row.notes || null,
        material_spec: row.material_spec ?? null,
        material_spec_raw: row.material_spec_raw || row.spec || null,
        contributions: [contrib],
      })
    }
  }

  return Array.from(bucketMap.values())
}

/**
 * Canonical H1 key for grouping material requirements:
 * material_id + normalized UOM + canonical material_spec serialization.
 * Never uses material_code or material_name for identity.
 */
export function getH1BucketKey(
  material_id: string | null | undefined,
  uom: string | null | undefined,
  spec: Record<string, unknown> | null | undefined,
): string {
  if (!material_id) return ''
  const normUom = (uom || '').trim().toLowerCase()
  const specKey = serializeSpecKey(spec)
  return `${material_id}::${normUom}::${specKey}`
}

export type ManualPriceConflict = {
  h1Key: string
  material_id: string
  material_name: string
  uom: string
  prices: number[]
  rowIds: string[]
}

/**
 * Detects conflicts where multiple preview rows sharing the same H1 material key
 * have different manual prices entered.
 */
export function detectManualPriceConflicts(
  rows: BomPreviewTableRow[],
): ManualPriceConflict[] {
  const groups = new Map<string, BomPreviewTableRow[]>()
  for (const row of rows) {
    if (!row.material_id) continue
    const key = getH1BucketKey(row.material_id, row.uom, row.material_spec)
    if (!key) continue
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(row)
  }

  const conflicts: ManualPriceConflict[] = []
  for (const [h1Key, groupRows] of groups.entries()) {
    const manualPrices = new Set<number>()
    const manualRowIds: string[] = []
    for (const r of groupRows) {
      if (
        r.price_source === 'manual' &&
        typeof r.unit_price === 'number' &&
        !isNaN(r.unit_price) &&
        r.unit_price > 0
      ) {
        manualPrices.add(r.unit_price)
        manualRowIds.push(r.id)
      }
    }
    if (manualPrices.size > 1) {
      const first = groupRows[0]!
      conflicts.push({
        h1Key,
        material_id: first.material_id!,
        material_name: first.material_name,
        uom: first.uom,
        prices: Array.from(manualPrices).sort((a, b) => a - b),
        rowIds: manualRowIds,
      })
    }
  }
  return conflicts
}

/**
 * Pure helper: Synchronizes manual price (unit_price & manual_reason) from a trigger row
 * to all other preview rows sharing the same H1 material key (material_id + uom + spec_key).
 * Rows with different spec_key or material_id are strictly preserved untouched.
 */
export function syncManualPricesAcrossSameMaterialRows(
  rows: BomPreviewTableRow[],
  triggerRowId: string,
): BomPreviewTableRow[] {
  const triggerRow = rows.find((r) => r.id === triggerRowId)
  if (!triggerRow || !triggerRow.material_id) return rows

  const targetH1Key = getH1BucketKey(
    triggerRow.material_id,
    triggerRow.uom,
    triggerRow.material_spec,
  )
  if (!targetH1Key) return rows

  const isManual = triggerRow.price_source === 'manual'
  const newPrice = triggerRow.unit_price
  const newReason =
    triggerRow.manual_reason?.trim() || 'Nhập tay khi tạo PO từ BOM'

  return rows.map((r) => {
    if (r.id === triggerRowId) return r

    const rKey = getH1BucketKey(r.material_id, r.uom, r.material_spec)
    if (rKey !== targetH1Key) return r

    // If trigger row entered a positive manual price, sync to this row
    if (isManual && typeof newPrice === 'number' && newPrice > 0) {
      const qty = Number(r.qty_actual_buy) || 0
      return {
        ...r,
        unit_price: newPrice,
        line_total: Math.round(newPrice * qty),
        price_source: 'manual' as const,
        selected_rule_id: null,
        manual_reason: newReason,
        price_warning: null,
        quote_status: 'MATCHED',
      }
    }

    // If trigger row cleared manual price
    if (isManual && (newPrice == null || newPrice <= 0)) {
      return {
        ...r,
        unit_price: null,
        line_total: 0,
        price_source: 'none' as const,
        selected_rule_id: null,
        manual_reason: '',
        price_warning: 'Chưa có đơn giá hợp lệ',
        quote_status: 'NONE',
      }
    }

    return r
  })
}

/**
 * Pure helper: Build CreatePoLinePayload[] from batch lines and preview rows.
 * Guarantees NEVER sending manual_price: 0 and NEVER including unresolved lines.
 * Strictly prevents order-dependence when merging same-material rows.
 */
export function buildPoLinesFromBatchAndPreview(
  batchLines: Array<{
    id: string
    material_id: string | null
    qty_actual_buy: number
    uom: string
    material_spec?: Record<string, unknown> | null
    material_spec_raw?: string | null
  }>,
  previewRows: BomPreviewTableRow[],
): CreatePoLinePayload[] {
  // 1. Guard against unresolved lines in batchLines
  for (const bLine of batchLines) {
    if (!bLine.material_id) {
      throw new Error(
        `Không thể tạo PO line cho dòng chưa khớp mã vật tư (unresolved): ${bLine.id}`,
      )
    }
  }

  // 2. Guard against manual price conflicts across rows sharing same H1 key
  const conflicts = detectManualPriceConflicts(previewRows)
  if (conflicts.length > 0) {
    const c = conflicts[0]!
    throw new Error(
      `Xung đột đơn giá tay cho vật tư "${c.material_name || c.material_id}": các dòng có giá khác nhau (${c.prices.join(' đ vs ')} đ). Vui lòng chọn một giá trước khi tạo PO.`,
    )
  }

  return batchLines.map((bLine) => {
    const bNormUom = (bLine.uom || '').trim().toLowerCase()
    const bSpecKey = serializeSpecKey(bLine.material_spec)
    
    // Find all matching preview rows for this H1 bucket
    const matchedRows = previewRows.filter(
      (r) =>
        r.material_id === bLine.material_id &&
        (r.uom || '').trim().toLowerCase() === bNormUom &&
        serializeSpecKey(r.material_spec) === bSpecKey,
    )

    if (matchedRows.length === 0) {
      throw new Error(
        `Không tìm thấy dòng preview tương ứng với vật tư ${bLine.material_id || 'chưa liên kết'}, ĐVT ${bLine.uom}, spec [${bSpecKey}]`,
      )
    }

    if (matchedRows.some((r) => r.resolution_status === 'unresolved' || !r.material_id)) {
      throw new Error(
        `Dòng vật tư ${bLine.material_id} ở trạng thái chưa phân giải (unresolved)`,
      )
    }

    // Resolve unified price: manual price takes precedence if set, otherwise auto rule
    const manualRow = matchedRows.find(
      (r) =>
        r.price_source === 'manual' &&
        typeof r.unit_price === 'number' &&
        r.unit_price > 0,
    )
    const autoRow = matchedRows.find(
      (r) => r.price_source === 'auto' && Boolean(r.selected_rule_id),
    )
    const sourceRow = manualRow ?? autoRow ?? matchedRows[0]!

    const specObj = bLine.material_spec ?? sourceRow.material_spec ?? null
    const specBase = bLine.material_spec_raw ?? sourceRow.spec ?? null
    const colorText =
      specObj && typeof specObj.color === 'string' ? specObj.color.trim() : ''
    const specRaw = colorText
      ? [specBase, `Màu: ${colorText}`].filter(Boolean).join(' - ')
      : specBase

    const hasRule = Boolean(sourceRow.selected_rule_id && sourceRow.price_source === 'auto')

    const baseLine: CreatePoLinePayload = {
      material_id: bLine.material_id!,
      order_qty: Number(bLine.qty_actual_buy),
      order_uom: bLine.uom,
      requirement_line_id: bLine.id,
      ...(specRaw ? { spec_text: specRaw } : {}),
      ...(specObj ? { spec_attributes: specObj } : {}),
      ...(typeof specObj?.thickness === 'number'
        ? { thick: specObj.thickness }
        : typeof specObj?.thick === 'number'
          ? { thick: specObj.thick }
          : {}),
      ...(typeof specObj?.width === 'number' ? { width: specObj.width } : {}),
      ...(typeof specObj?.length === 'number' ? { length: specObj.length } : {}),
      ...(typeof specObj?.quality === 'string' ? { quality: specObj.quality } : {}),
    }

    if (hasRule && sourceRow?.selected_rule_id) {
      return {
        ...baseLine,
        selected_rule_id: sourceRow.selected_rule_id,
      }
    }

    const price = sourceRow?.unit_price != null ? Number(sourceRow.unit_price) : 0
    if (price <= 0 || sourceRow?.price_source !== 'manual') {
      return { ...baseLine, price_pending: true }
    }
    return {
      ...baseLine,
      manual_price: price,
      manual_reason: sourceRow?.manual_reason || 'Nhập tay khi tạo PO từ BOM',
    }
  })
}

export function PoCreateFromBomPage() {

  // State: Masters
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loadingMasters, setLoadingMasters] = useState(true)

  const [activeSheetRules, setActiveSheetRules] = useState<ActiveRuleOption[]>([])
  const [rulePickerRowId, setRulePickerRowId] = useState<string | null>(null)
  const [createdSupplierPos, setCreatedSupplierPos] = useState<Array<{ supplierId: string; supplierName: string; id: string; code: string; lineCount: number; total: number }>>([])
  const [materialBatch, setMaterialBatch] = useState<MaterialRequirementBatchRow | null>(null)
  const materialBatchSignatureRef = useRef<string | null>(null)
  const rowReqSeqRef = useRef<Map<string, number>>(new Map())
  const rowSignatureRef = useRef<Map<string, string>>(new Map())
  const rowTimerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    return () => {
      for (const timer of rowTimerRef.current.values()) {
        clearTimeout(timer)
      }
      rowTimerRef.current.clear()
    }
  }, [])

  function invalidateBomRow(rowId: string): number {
    const existingTimer = rowTimerRef.current.get(rowId)
    if (existingTimer) {
      clearTimeout(existingTimer)
      rowTimerRef.current.delete(rowId)
    }
    const nextSeq = (rowReqSeqRef.current.get(rowId) || 0) + 1
    rowReqSeqRef.current.set(rowId, nextSeq)
    rowSignatureRef.current.delete(rowId)
    return nextSeq
  }

  // State: Form Inputs (Steps 1-3)
  const [bomSource, setBomSource] = useState<CanonicalBomSource>('foam')
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState<string>(() => {
    return new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10)
  })
  const [poNote, setPoNote] = useState<string>('')
  const [selectedProducts, setSelectedProducts] = useState<SelectedProductRow[]>([])
  // Chọn mã hàng: vừa gõ vừa chọn, tìm phía server (xem ProductSearchPicker).
  const [productToAdd, setProductToAdd] = useState<Product | null>(null)

  // State: Product BOM Config Options for Hardware (Color & Wood Type)
  const [productConfigOptions, setProductConfigOptions] = useState<
    Record<string, ProductConfigOptionsState>
  >({})
  const fetchedProductIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (bomSource !== 'hardware') return
    const neededProductIds = Array.from(new Set(selectedProducts.map((p) => p.product_id)))
    for (const pid of neededProductIds) {
      if (fetchedProductIdsRef.current.has(pid)) continue
      fetchedProductIdsRef.current.add(pid)

      setProductConfigOptions((prev) => ({
        ...prev,
        [pid]: { loading: true, error: null, colors: [], woods: [] },
      }))

      const prodCode = selectedProducts.find((p) => p.product_id === pid)?.product_code
      getProductBomConfigsApi(pid, prodCode || '')
        .then((configs) => {
          const { colors, woods } = extractProductBomColorAndWoodOptions(configs)
          setProductConfigOptions((prev) => ({
            ...prev,
            [pid]: { loading: false, error: null, colors, woods },
          }))
        })
        .catch((err) => {
          fetchedProductIdsRef.current.delete(pid)
          setProductConfigOptions((prev) => ({
            ...prev,
            [pid]: {
              loading: false,
              error: err instanceof Error ? err.message : 'Lỗi tải cấu hình BOM',
              colors: [],
              woods: [],
            },
          }))
        })
    }
  }, [bomSource, selectedProducts])

  // Mút xốp: chỉ các cấu hình BOM mã hàng thật sự có (Kết cấu - Đóng gói gộp một ô).
  const [foamChoices, setFoamChoices] = useState<
    Record<string, { loading: boolean; error: string | null; choices: FoamChoice[] }>
  >({})
  const foamFetchedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (bomSource !== 'foam') return
    const ids = Array.from(new Set(selectedProducts.map((p) => p.product_id)))
    for (const pid of ids) {
      if (foamFetchedRef.current.has(pid)) {
        // Mã đã tải cấu hình (ví dụ xóa rồi thêm lại): vẫn áp quy tắc "đúng 1 cấu hình thì tự lấy".
        const known = foamChoices[pid]
        const auto = known && !known.loading && !known.error ? pickAutoFoamChoice(known.choices) : null
        if (auto && selectedProducts.some((row) => row.product_id === pid && !row.assembly_type)) {
          setSelectedProducts((prev) =>
            prev.map((row) =>
              row.product_id === pid && !row.assembly_type
                ? { ...row, assembly_type: auto.assembly, packaging_type: auto.packaging }
                : row,
            ),
          )
        }
        continue
      }
      foamFetchedRef.current.add(pid)
      setFoamChoices((prev) => ({ ...prev, [pid]: { loading: true, error: null, choices: [] } }))
      getProductFoamBomConfigsApi(pid)
        .then((configs) => {
          const choices = buildFoamChoices(configs)
          setFoamChoices((prev) => ({ ...prev, [pid]: { loading: false, error: null, choices } }))
          const auto = pickAutoFoamChoice(choices)
          if (auto) {
            // Đúng 1 cấu hình: tự lấy. Chỉ điền cho dòng chưa chọn, không ghi đè lựa chọn của người dùng.
            setSelectedProducts((prev) =>
              prev.map((row) =>
                row.product_id === pid && !row.assembly_type
                  ? { ...row, assembly_type: auto.assembly, packaging_type: auto.packaging }
                  : row,
              ),
            )
          }
        })
        .catch((err) => {
          foamFetchedRef.current.delete(pid)
          setFoamChoices((prev) => ({
            ...prev,
            [pid]: {
              loading: false,
              error: err instanceof Error ? err.message : 'Lỗi tải cấu hình BOM đóng gói',
              choices: [],
            },
          }))
        })
    }
  }, [bomSource, selectedProducts, foamChoices])

  // State: Resolving & Preview (Steps 4-6)
  const [isResolving, setIsResolving] = useState(false)
  const [resolveError, setResolveError] = useState<string | null>(null)
  const [previewRows, setPreviewRows] = useState<BomPreviewTableRow[]>([])
  const [configurationCollapsed, setConfigurationCollapsed] = useState(false)
  const calculateSheetRowPrice = async (rowId: string, rule: ActiveRuleOption, inputs: Record<string, number>) => {
    setPreviewRows((current) => current.map((row) => row.id === rowId ? { ...row, selected_rule_id: rule.id, rule_label: rule.supplier_item_name, sheet_inputs: inputs, unit_price: null, line_total: 0, quote_status: 'RESOLVING', price_warning: null } : row))
    try {
      const response = await apiFetch(`/api/quote-rules/${encodeURIComponent(rule.id)}/sheet-calc`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ inputs }) })
      const body = await response.json() as { ok?: boolean; snapshot?: { purchase_price?: number }; error?: { message?: string } }
      if (!response.ok || !body.ok || !Number.isFinite(Number(body.snapshot?.purchase_price))) throw new Error(body.error?.message || 'Không tính được giá từ bảng báo giá')
      const price = Number(body.snapshot!.purchase_price)
      setPreviewRows((current) => current.map((row) => row.id === rowId ? { ...row, unit_price: price, line_total: price * Number(row.qty_actual_buy), price_source: 'auto', quote_status: 'MATCHED', price_warning: null } : row))
    } catch (error) {
      setPreviewRows((current) => current.map((row) => row.id === rowId ? { ...row, quote_status: 'ERROR', price_warning: error instanceof Error ? error.message : 'Không tính được giá' } : row))
    }
  }

  // Bảng kết quả mặc định chỉ xem. Phải bấm "Sửa" mới sửa/loại trừ được, tránh bấm nhầm hoặc xóa nhầm.
  const [isEditingRows, setIsEditingRows] = useState(false)
  const editSnapshotRef = useRef<BomPreviewTableRow[] | null>(null)
  const resolveBomReqSeqRef = useRef<number>(0)
  const resolveBomSignatureRef = useRef<string>('')

  function invalidateResolveBom() {
    resolveBomReqSeqRef.current++
    resolveBomSignatureRef.current = ''
    setIsResolving(false)
  }

  // State: Ambiguous Candidates Modal
  const [ambiguousData, setAmbiguousData] = useState<{
    isOpen: boolean
    product_id: string
    product_code: string
    reason: string
    candidates: Array<{
      id: string
      config_name?: string
      variant_name?: string
      assembly_type?: string
      packaging_type?: string
      color?: string
      created_at?: string
    }>
    isFoam: boolean
  } | null>(null)

  // State: Confirm Remove Unresolved Modal
  const [isRemoveUnresolvedModalOpen, setIsRemoveUnresolvedModalOpen] = useState(false)

  // State: Creating PO (Step 7)
  const [isCreatingPo, setIsCreatingPo] = useState(false)
  const [createPoError, setCreatePoError] = useState<string | null>(null)

  // Load masters on mount
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const supList = await supplierService.list()
        if (cancelled) return
        setSuppliers(supList.filter((s) => s.status === 'ACTIVE'))
      } catch (err) {
        console.error('Failed to load masters:', err)
      } finally {
        if (!cancelled) setLoadingMasters(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  // Auto-detect bom_source and load active quotes/rules when supplier is picked
  // Add product to table
  const handleAddProduct = () => {
    const prod = productToAdd
    if (!prod) return
    if (selectedProducts.some((row) => row.product_id === prod.id)) {
      setResolveError(`Mã hàng ${prod.code} đã có trong danh sách. Sửa số lượng ở dòng hiện có.`)
      return
    }

    invalidateResolveBom()
    const newRow: SelectedProductRow = {
      id: `prod-row-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      product_id: prod.id,
      product_code: prod.code,
      product_name: prod.name,
      order_qty: 0,
      assembly_type: bomSource === 'foam' ? '' : 'kd',
      packaging_type: undefined,
      color: '',
      wood_type: '',
    }
    setSelectedProducts((prev) => [...prev, newRow])
    setProductToAdd(null)
    setResolveError(null)
  }

  // Remove product from table
  const handleRemoveProduct = (rowId: string) => {
    invalidateResolveBom()
    setSelectedProducts((prev) => prev.filter((p) => p.id !== rowId))
    setResolveError(null)
  }

  // Update selected product row
  const handleUpdateProduct = (
    rowId: string,
    updates: Partial<SelectedProductRow>,
  ) => {
    invalidateResolveBom()
    setSelectedProducts((prev) =>
      prev.map((p) => (p.id === rowId ? { ...p, ...updates } : p)),
    )
    setResolveError(null)
  }

  const handleResolveBom = async () => {
    setResolveError(null)
    setCreatePoError(null)
    setIsEditingRows(false)
    editSnapshotRef.current = null

    const validationMsg = validateProductInputs(null, selectedProducts, bomSource)
    if (validationMsg) {
      setResolveError(validationMsg)
      return
    }

    const nextSeq = ++resolveBomReqSeqRef.current
    const sig = buildResolveBomSignature({
      supplierId: null,
      deliveryDate: expectedDeliveryDate,
      bomSource,
      products: selectedProducts,
    })
    resolveBomSignatureRef.current = sig

    const inputs = buildExplosionInputs(selectedProducts, bomSource)
    setIsResolving(true)

    try {
      const result = await resolveBomAdapterApi(inputs)
      if (
        resolveBomReqSeqRef.current !== nextSeq ||
        resolveBomSignatureRef.current !== sig
      ) {
        return
      }

      const rawRows = mapExplosionResultToPreviewRows(result.lines)
      if (rawRows.length === 0) {
        setPreviewRows([])
        setResolveError(buildEmptyBomMessage(selectedProducts, bomSource))
        return
      }
      const itemIds = [...new Set(rawRows.map((r) => r.material_id).filter((id): id is string => Boolean(id)))]
      const params = new URLSearchParams({ item_ids: itemIds.join(',') })
      const rulesRes = await apiFetch(`/api/active-sheet-rules?${params.toString()}`)
      const rulesBody = await rulesRes.json() as { ok?: boolean; rules?: ActiveRuleOption[]; error?: { message?: string } }
      if (!rulesRes.ok || !rulesBody.ok) throw new Error(rulesBody.error?.message || 'Không tải được báo giá bảng tính ACTIVE')
      const unresolvedNames = [...new Set(rawRows.filter((row) => row.resolution_status === 'unresolved' || (row.bom_source === 'foam' && row.material_spec?.standard_foam_bom === true)).map((row) => String(row.material_spec?.item_name ?? row.bom_material_name ?? row.material_name).trim()).filter(Boolean))]
      const searchedRules = await Promise.all(unresolvedNames.map(async (name) => {
        const search = new URLSearchParams({ q: name })
        const response = await apiFetch(`/api/active-sheet-rules?${search.toString()}`)
        const body = await response.json() as { ok?: boolean; rules?: ActiveRuleOption[]; error?: { message?: string } }
        if (!response.ok || !body.ok) throw new Error(body.error?.message || `Không tìm được báo giá ACTIVE cho ${name}`)
        return body.rules ?? []
      }))
      const loadedRules = [...(rulesBody.rules ?? []), ...searchedRules.flat()].filter((rule, index, all) => all.findIndex((candidate) => candidate.id === rule.id) === index)
      setActiveSheetRules(loadedRules)
      const matchedRows = rawRows.map((row) => {
        const standardFoamRow = row.bom_source === 'foam' && row.material_spec?.standard_foam_bom === true
        const rawBomName = String(row.material_spec?.item_name ?? row.bom_material_name ?? row.material_name).normalize('NFC').trim()
        const rowRules = loadedRules.filter((rule) => standardFoamRow
          ? rule.supplier_item_name.normalize('NFC').trim() === rawBomName && (!rule.item_group_code || rule.item_group_code === 'bb')
          : row.material_id
            ? rule.item_id === row.material_id
          : (rule.item_name?.toLocaleLowerCase('vi').includes(row.material_name.toLocaleLowerCase('vi'))
            || rule.supplier_item_name.toLocaleLowerCase('vi').includes(row.material_name.toLocaleLowerCase('vi')))
            && (!rule.item_group_code || rule.item_group_code === (row.bom_source === 'hardware' ? 'nk' : row.bom_source === 'foam' ? 'bb' : 'td'))) as unknown as SheetRuleForBomMatch[]
        const assignment = assignSupplier(rowRules.map((rule) => ({ id: rule.supplier_id, name: rule.supplier_name })))
        const supplierChoices = assignment.candidates.length
          ? assignment.candidates
          : suppliers.filter((supplier) => supplier.status === 'ACTIVE').map((supplier) => ({ id: supplier.id, name: supplier.name }))
        const supplierRules = rowRules.filter((rule) => rule.supplier_id === assignment.supplierId)
        const mappedBom = bomSheetValues({ material_id: row.material_id, bom_source: row.bom_source, material_name: standardFoamRow ? rawBomName : row.material_name, material_spec: row.material_spec })
        const match = matchSheetRules(supplierRules, {
          material_id: row.material_id,
          bom_source: row.bom_source,
          material_name: standardFoamRow ? rawBomName : row.material_name,
          material_spec: row.material_spec,
        })
        const hasMatchingStandardFoamQuote = standardFoamRow && matchSheetRules(rowRules, {
          material_id: row.material_id,
          bom_source: row.bom_source,
          material_name: rawBomName,
          material_spec: row.material_spec,
        }).rules.length > 0
        const chosen = match.selectedRule
        const chosenInputNames = (chosen?.input_names ?? []).filter((name) => name.trim() !== 'ĐV đơn giá')
        const previewRow = {
          ...row,
          uom: row.resolution_status === 'resolved' ? 'Pcs' : row.uom,
          supplier_id: assignment.supplierId,
          supplier_name: assignment.supplierName,
          supplier_candidates: supplierChoices,
          supplier_requires_confirmation: assignment.requiresConfirmation,
          selected_rule_id: chosen?.id ?? null,
          sheet_inputs: chosen ? Object.fromEntries(chosenInputNames.flatMap((name) => {
            const raw = mappedBom.inputs[name] ?? mappedBom.values[name]
            const value = Number(raw)
            return Number.isFinite(value) ? [[name, value]] : []
          })) : {},
          sheet_input_names: chosenInputNames,
          rule_label: chosen?.supplier_item_name,
          quote_status: assignment.supplierId ? (chosen ? (match.missingInputs.length ? 'MISSING_INPUT' : 'RESOLVING') : 'NEEDS_RULE') : 'NO_QUOTE',
          price_warning: !assignment.supplierId ? 'Chưa có báo giá' : chosen && match.missingInputs.length ? `Thiếu ${match.missingInputs.join(', ')}` : null,
          purchase_inputs: mappedBom.values,
          config_label: bomSource === 'foam'
            ? [assemblyTypeLabel(row.bom_assembly), istaLevelLabel(row.bom_packaging === 'thuong' ? '1A' : row.bom_packaging === 'ecom' ? '3A' : '')].filter(Boolean).join(' - ')
            : [selectedProducts.find((product) => product.product_id === row.product_id)?.color || row.color, assemblyTypeLabel(row.bom_assembly)].filter(Boolean).join(' - '),
        } as BomPreviewTableRow
        return requireStandardFoamQuoteMatch(previewRow, hasMatchingStandardFoamQuote)
      })
      setPreviewRows(matchedRows)
      setConfigurationCollapsed(true)
      await Promise.all(matchedRows.filter((row) => row.selected_rule_id && !(row.sheet_input_names ?? []).some((name) => row.sheet_inputs?.[name] == null)).map(async (row) => {
        try {
          const res = await apiFetch(`/api/quote-rules/${encodeURIComponent(row.selected_rule_id!)}\u002fsheet-calc`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ inputs: row.sheet_inputs ?? {} }) })
          const body = await res.json() as { ok?: boolean; snapshot?: { purchase_price?: number }; error?: { message?: string } }
          if (!res.ok || !body.ok || !Number.isFinite(Number(body.snapshot?.purchase_price))) throw new Error(body.error?.message || 'Không tính được giá từ bảng báo giá')
          setPreviewRows((current) => current.map((r) => r.id === row.id ? { ...r, unit_price: Number(body.snapshot!.purchase_price), line_total: Number(body.snapshot!.purchase_price) * Number(r.qty_actual_buy), price_source: 'auto', quote_status: 'MATCHED', price_warning: null } : r))
        } catch (error) {
          setPreviewRows((current) => current.map((r) => r.id === row.id ? { ...r, quote_status: 'ERROR', price_warning: error instanceof Error ? error.message : 'Không tính được giá' } : r))
        }
      }))

      if (
        resolveBomReqSeqRef.current !== nextSeq ||
        resolveBomSignatureRef.current !== sig
      ) {
        return
      }

      // Sheet rules and prices are assigned per material row above.
      // Một số mã có dòng, một số mã không có dòng nào: báo rõ mã nào để người dùng không tưởng đã đủ.
      const codesWithLines = new Set(rawRows.map((r) => r.product_id))
      const missing = selectedProducts.filter((p) => !codesWithLines.has(p.product_id))
      if (missing.length > 0) {
        setResolveError(buildEmptyBomMessage(missing, bomSource, true))
      }
    } catch (err: any) {
      if (
        resolveBomReqSeqRef.current !== nextSeq ||
        resolveBomSignatureRef.current !== sig
      ) {
        return
      }

      setPreviewRows([])
      if (err instanceof ProcurementApiClientError) {
        if (err.code === 'CONFIG_AMBIGUOUS') {
          const details = err.details
          const ambObj = details?.ambiguous as any
          const isFoam = Boolean(ambObj?.foam)
          const candidates = ambObj?.foam?.candidates || ambObj?.wood?.candidates || []
          const reason = ambObj?.foam?.reason || ambObj?.wood?.reason || err.message

          setAmbiguousData({
            isOpen: true,
            product_id: String(details?.product_id || ''),
            product_code: String(details?.product_code || ''),
            reason,
            candidates,
            isFoam,
          })
          setResolveError(err.message)
          return
        }
        if (err.code === 'CONFIG_NOT_FOUND') {
          setResolveError(`${err.message} — Vui lòng kiểm tra cấu hình BOM hoặc xóa mã hàng này.`)
          return
        }
        setResolveError(err.message)
      } else {
        setResolveError(err.message || 'Lỗi không xác định khi gọi BOM Adapter')
      }
    } finally {
      if (resolveBomReqSeqRef.current === nextSeq) {
        setIsResolving(false)
      }
    }
  }

  // Handle ambiguous selection override
  const handleSelectAmbiguousCandidate = (candidateId: string) => {
    if (!ambiguousData) return

    invalidateResolveBom()
    setSelectedProducts((prev) =>
      prev.map((p) => {
        if (p.product_id === ambiguousData.product_id) {
          return {
            ...p,
            override_foam_config_id: ambiguousData.isFoam ? candidateId : p.override_foam_config_id,
            override_wood_config_id: !ambiguousData.isFoam ? candidateId : p.override_wood_config_id,
          }
        }
        return p
      }),
    )

    setAmbiguousData(null)
    setResolveError(null)
  }

  // Reset override config on a product
  const handleResetOverride = (rowId: string) => {
    invalidateResolveBom()
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.id === rowId
          ? { ...p, override_foam_config_id: undefined, override_wood_config_id: undefined }
          : p,
      ),
    )
  }

  // Count unresolved rows
  const unresolvedCount = useMemo(() => {
    return previewRows.filter((r) => r.resolution_status === 'unresolved').length
  }, [previewRows])

  // Count rows missing price
  const missingPriceCount = useMemo(() => {
    return previewRows.filter((row) => row.resolution_status === 'resolved').filter(
      (r) => r.unit_price == null || typeof r.unit_price !== 'number' || isNaN(r.unit_price) || r.unit_price <= 0,
    ).length
  }, [previewRows])
  const defaultHiddenBomColumns = useMemo(() => getDefaultHiddenBomColumns(previewRows, bomSource), [bomSource, previewRows])
  const pricedResolvedCount = useMemo(() => countPricedResolvedRows(previewRows), [previewRows])

  // Detect manual price conflicts
  const priceConflicts = useMemo(() => {
    return detectManualPriceConflicts(previewRows)
  }, [previewRows])

  // Count same material rows for manual price sync indication
  const sameMaterialCounts = useMemo(() => {
    const map = new Map<string, number>()
    for (const r of previewRows) {
      if (!r.material_id) continue
      const k = getH1BucketKey(r.material_id, r.uom, r.material_spec)
      if (k) map.set(k, (map.get(k) || 0) + 1)
    }
    return map
  }, [previewRows])

  const handleStartEditRows = () => {
    editSnapshotRef.current = previewRows.map((r) => ({ ...r }))
    setIsEditingRows(true)
  }

  const handleConfirmEditRows = () => {
    editSnapshotRef.current = null
    setIsEditingRows(false)
  }

  const handleCancelEditRows = () => {
    const snapshot = editSnapshotRef.current
    if (snapshot) {
      // Hủy mọi lần tính giá đang chờ của các dòng vừa sửa để không ghi đè lại bản đã khôi phục.
      for (const r of previewRows) invalidateBomRow(r.id)
      setPreviewRows(snapshot)
    }
    editSnapshotRef.current = null
    setIsEditingRows(false)
  }

  // Remove a single row (unresolved or unwanted) from previewRows
  const handleRemoveRow = (rowId: string) => {
    invalidateBomRow(rowId)
    setPreviewRows((prev) => prev.filter((r) => r.id !== rowId))
  }

  // Remove all unresolved rows
  const handleRemoveAllUnresolved = () => {
    setPreviewRows((prev) => prev.filter((r) => r.resolution_status === 'resolved'))
    setIsRemoveUnresolvedModalOpen(false)
  }

  // Step 7: Create Material Requirements Batch & Purchase Order
  const handleCreatePo = async () => {
    setCreatePoError(null)
    if (previewRows.length === 0) {
      setCreatePoError('Chưa có danh sách vật tư để tạo đơn PO')
      return
    }
    const unassigned = previewRows.filter((row) => row.resolution_status === 'resolved' && !row.supplier_id)
    if (unassigned.length) {
      setCreatePoError(`Chọn NCC cho ${unassigned.length} dòng vật tư chưa được gán nhà cung cấp.`)
      return
    }
    const assignedRows = rowsEligibleForBomPurchaseOrders(previewRows)
    const needingConfirmation = assignedRows.filter((row) => row.supplier_requires_confirmation)
    if (needingConfirmation.length) { setCreatePoError('Hãy xác nhận NCC cho các dòng có nhiều báo giá.'); return }
    // Validate qty_actual_buy
    for (let i = 0; i < assignedRows.length; i++) {
      const r = assignedRows[i]
      if (typeof r.qty_actual_buy !== 'number' || isNaN(r.qty_actual_buy) || r.qty_actual_buy <= 0) {
        setCreatePoError(
          `Dòng ${i + 1} (${r.material_name}): Số lượng mua thực tế phải là số dương lớn hơn 0`,
        )
        return
      }
    }

    // Validate unit_price (Fix Bug manual_price=0)
    if (!assignedRows.length) { setCreatePoError('Không có dòng hợp lệ để tạo PO.'); return }

    setIsCreatingPo(true)
    try {
      const batchLines = aggregatePreviewRowsToBatchLines(previewRows)
      const inputs = buildExplosionInputs(selectedProducts, bomSource)
      const batchSignature = JSON.stringify({ bomSource, inputs, lines: batchLines })
      const reuseMaterialBatch = Boolean(materialBatch && isSameBomBatchRun(materialBatchSignatureRef.current, batchSignature))
      const supplierPosForRun = reuseMaterialBatch ? createdSupplierPos : []
      if (!reuseMaterialBatch) {
        setMaterialBatch(null)
        setCreatedSupplierPos([])
      }
      const prodCodes = Array.from(new Set(selectedProducts.map((p) => p.product_code))).join(', ')
      const batchTitle = `Nhu cầu ${bomSource === 'foam' ? 'Bao bì + Mút xốp' : 'Ngũ kim'} (${prodCodes}) - ${formatDateFull(new Date())}`

      // 1. Tạo Material Requirement Batch Snapshot
      const batch = reuseMaterialBatch ? materialBatch! : await createMaterialRequirementBatchApi({
        title: batchTitle,
        bom_source: bomSource,
        supplier_id: null,
        input_snapshot: inputs,
        lines: batchLines,
      })
      if (!reuseMaterialBatch) {
        setMaterialBatch(batch)
        materialBatchSignatureRef.current = batchSignature
      }

      // 2. Chuyển đổi sang CreatePoLinePayload[] kèm requirement_line_id và ĐƠN GIÁ HỢP LỆ (Fix manual_price)
      const returnedLines = batch.lines || []
      const groups = [...new Set(assignedRows.map((row) => row.supplier_id!))]
      const errors: string[] = []
      for (const supplierId of groups) {
        if (supplierPosForRun.some((po) => po.supplierId === supplierId)) continue
        const groupRows = assignedRows.filter((row) => row.supplier_id === supplierId)
        const lines: CreatePoLinePayload[] = groupBomPoLines(groupRows.map((row) => {
          const batchLine = returnedLines.find((line) => line.contributions?.some((contribution) => contribution.product_id === row.product_id && contribution.product_code === row.product_code && contribution.source_bom_line_id === row.source_bom_line_id))
          if (!batchLine) throw new Error(`Không tìm thấy dòng nhu cầu vật tư cho ${row.product_code} / ${row.material_name}`)
          const baseLine: CreatePoLinePayload = {
            material_id: row.material_id!, spec_text: row.spec || null, spec_attributes: row.material_spec ?? undefined,
            order_qty: Number(row.qty_actual_buy), order_uom: 'Pcs', requirement_line_id: batchLine.id,
          }
          if (row.price_source === 'manual' && row.unit_price != null && row.unit_price > 0) {
            return { ...baseLine, manual_price: Number(row.unit_price), manual_reason: row.manual_reason?.trim() || 'Nhập tay khi tạo PO từ BOM' }
          }
          if (row.selected_rule_id && row.unit_price != null && row.unit_price > 0 && row.price_source === 'auto') {
            return { ...baseLine, selected_rule_id: row.selected_rule_id, sheet_inputs: row.sheet_inputs ?? {} }
          }
          return { ...baseLine, price_pending: true }
        }))
        try {
          const created = await createPurchaseOrder({ supplier_id: supplierId, delivery_date: expectedDeliveryDate, note: poNote ? `Từ BOM Batch ${batch.id}: ${poNote}` : `Tạo tự động từ BOM Batch ${batch.id}`, lines })
          const sup = suppliers.find((item) => item.id === supplierId)
          const total = groupRows.reduce((sum, row) => sum + Number(row.line_total || 0), 0)
          setCreatedSupplierPos((current) => current.some((po) => po.supplierId === supplierId) ? current : [...current, { supplierId, supplierName: sup?.name ?? supplierId, id: created.id, code: created.code || created.id, lineCount: lines.length, total }])
        } catch (error) {
          errors.push(`${suppliers.find((item) => item.id === supplierId)?.name ?? supplierId}: ${error instanceof Error ? error.message : 'lỗi không xác định'}`)
          break
        }
      }
      setCreatePoError(errors.length ? `Đã tạo một phần. ${errors.join('; ')}. Bấm tạo lại để tiếp tục các NCC còn lại.` : null)
    } catch (err: any) {
      console.error('Error creating PO from BOM:', err)
      setCreatePoError(err.message || 'Lỗi không xác định khi tạo đơn mua hàng')
    } finally {
      setIsCreatingPo(false)
    }
  }

  // Handle table data edit
  const handleTableChange = (newData: BomPreviewTableRow[]) => {
    const rowsToSchedule: BomPreviewTableRow[] = []
    let manualTriggerRowId: string | null = null

    // 1. Detect which row had manual price or reason changed by user
    for (const row of newData) {
      const oldRow = previewRows.find((r) => r.id === row.id)
      if (!oldRow) continue
      if (
        row.unit_price !== oldRow.unit_price ||
        (row.price_source === 'manual' && row.manual_reason !== oldRow.manual_reason)
      ) {
        manualTriggerRowId = row.id
        break
      }
    }

    let updated = newData.map((row) => {
      const oldRow = previewRows.find((r) => r.id === row.id)
      if (!oldRow) return row

      const numPrice = Number(row.unit_price)
      const qty = Number(row.qty_actual_buy)

      // 1. User edited unit_price (Explicit manual price entry)
      if (row.unit_price !== oldRow.unit_price) {
        invalidateBomRow(row.id)
        if (numPrice && numPrice > 0) {
          const provisionalTotal = Math.round(numPrice * (qty || 0))
          return {
            ...row,
            unit_price: numPrice,
            line_total: provisionalTotal,
            price_source: 'manual' as const,
            selected_rule_id: null,
            manual_reason: row.manual_reason?.trim() || oldRow.manual_reason?.trim() || 'Nhập tay khi tạo PO từ BOM',
            price_warning: null,
            quote_status: 'MATCHED',
          }
        } else {
          return {
            ...row,
            unit_price: null,
            line_total: 0,
            price_source: 'none' as const,
            selected_rule_id: null,
            manual_reason: '',
            price_warning: 'Chưa có đơn giá hợp lệ',
            quote_status: 'NONE',
          }
        }
      }

      // 2. User edited qty_actual_buy
      if (row.qty_actual_buy !== oldRow.qty_actual_buy) {
        if (oldRow.price_source === 'manual') {
          // Manual price row: provisional UI recalculation
          const provisionalTotal = Math.round((oldRow.unit_price || 0) * (qty || 0))
          return {
            ...row,
            unit_price: oldRow.unit_price,
            line_total: provisionalTotal,
            price_source: 'manual' as const,
            manual_reason: oldRow.manual_reason,
          }
        } else {
          // Auto price row: MUST invalidate client price and re-resolve authoritative server price!
          invalidateBomRow(row.id)
          const isQtyInvalid = isNaN(qty) || qty <= 0
          const updatedRow: BomPreviewTableRow = {
            ...row,
            unit_price: null,
            line_total: 0,
            price_source: 'none' as const,
            selected_rule_id: oldRow.selected_rule_id,
            manual_reason: '',
            quote_status: isQtyInvalid ? 'INVALID_QTY' : 'RESOLVING',
            price_warning: isQtyInvalid
              ? 'Số lượng mua thực tế phải lớn hơn 0'
              : 'Đang xác thực giá từ máy chủ...',
          }
          if (!isQtyInvalid && row.selected_rule_id && row.material_id) {
            rowsToSchedule.push(updatedRow)
          }
          return updatedRow
        }
      }

      // 3. User edited other fields (notes, manual_reason, etc.)
      return {
        ...row,
        // Keep authoritative line_total unless manual
        line_total: oldRow.price_source === 'manual'
          ? Math.round((Number(oldRow.unit_price) || 0) * (Number(row.qty_actual_buy) || 0))
          : oldRow.line_total,
      }
    })

    // 2. Automatically sync manual price across all rows sharing the same H1 key
    if (manualTriggerRowId) {
      updated = syncManualPricesAcrossSameMaterialRows(updated, manualTriggerRowId)
      const trigger = updated.find((r) => r.id === manualTriggerRowId)
      if (trigger?.material_id) {
        const triggerKey = getH1BucketKey(trigger.material_id, trigger.uom, trigger.material_spec)
        for (const r of updated) {
          if (r.id !== manualTriggerRowId && getH1BucketKey(r.material_id, r.uom, r.material_spec) === triggerKey) {
            invalidateBomRow(r.id)
          }
        }
      }
    }

    setPreviewRows(updated)

    // Recalculate each changed row with its selected supplier sheet rule.
    for (const r of rowsToSchedule) {
      const rule = activeSheetRules.find((item) => item.id === r.selected_rule_id)
      if (rule) void calculateSheetRowPrice(r.id, rule, r.sheet_inputs ?? {})
    }
  }

  // ExcelEditableTable columns for Step 5 & 6
  const tableColumns = useMemo<ExcelColumn<BomPreviewTableRow>[]>(() => {
    return [
      {
        key: 'id',
        label: 'STT',
        width: 48,
        stickyLeft: 0,
        align: 'center',
        readOnly: true,
        formatter: (_val, _row, idx) => String(idx + 1),
      },
      {
        key: 'product_code',
        label: 'Mã hàng',
        width: 130,
        stickyLeft: 48,
        type: 'text',
        align: 'left',
        readOnly: true,
        sortable: true,
        filterable: true,
      },
      {
        key: 'config_label',
        label: bomSource === 'foam' ? 'Kết cấu - Đóng gói' : 'Màu - Kết cấu',
        width: 150,
        type: 'text',
        align: 'left',
        readOnly: true,
        sortable: true,
        filterable: true,
        formatter: (_val, row) => String(row.config_label || ''),
      },
      {
        key: 'material_name',
        label: 'Tên vật tư',
        width: 220,
        type: 'text',
        align: 'left',
        readOnly: true,
        sortable: true,
        filterable: true,
        formatter: (val) => String(val || '—'),
        renderCell: (_val, row) => <span title={row.bom_material_name || row.material_name}>{row.material_name || '—'}</span>,
      },
      {
        key: 'dimensions',
        label: 'Quy cách',
        width: 130,
        type: 'text',
        align: 'left',
        readOnly: true,
        sortable: true,
        filterable: true,
        formatter: (val) => String(val || ''),
      },
      {
        key: 'supplier_name', label: 'NCC', width: 150, type: 'text', align: 'left', readOnly: true, sortable: true, filterable: true,
        renderCell: (_val, row) => row.supplier_candidates?.length ? (
          <div className="flex flex-col gap-1"><select className="w-full text-xs border rounded px-1 py-1" value={row.supplier_id ?? ''} onChange={(event) => {
            const candidate = row.supplier_candidates?.find((item) => item.id === event.target.value)
            setPreviewRows((current) => current.map((item) => item.id === row.id ? { ...item, supplier_id: candidate?.id ?? null, supplier_name: candidate?.name ?? null, supplier_requires_confirmation: false, selected_rule_id: null, rule_label: '', unit_price: null, line_total: 0, quote_status: 'NEEDS_RULE', price_warning: 'Chọn dòng báo giá' } : item))
          }}>{!row.supplier_id && <option value="">Chọn NCC</option>}{row.supplier_candidates.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}</select>{row.supplier_requires_confirmation && <button type="button" className="text-[10px] text-amber-700 underline" onClick={() => setPreviewRows((current) => current.map((item) => item.id === row.id ? { ...item, supplier_requires_confirmation: false } : item))}>Xác nhận NCC này</button>}</div>
        ) : <span className={row.supplier_id ? '' : 'text-red-600'}>{row.supplier_name || 'Chưa có báo giá'}</span>,
      },
      {
        key: 'rule_label', label: 'Dòng báo giá', width: 190, type: 'text', align: 'left', readOnly: true, sortable: true, filterable: true,
        renderCell: (_val, row) => <button type="button" className={row.selected_rule_id ? 'text-blue-700 underline text-left' : 'text-amber-700 underline text-left'} disabled={!row.supplier_id} onClick={() => setRulePickerRowId(row.id)}>{row.rule_label || 'Chọn dòng báo giá'}</button>,
        formatter: (val) => String(val || 'Chọn dòng báo giá'),
      },
      {
        key: 'sheet_inputs', label: 'Nhập lúc mua', width: 130, type: 'text', align: 'left', readOnly: true, sortable: false,
        renderCell: (_val, row) => (row.sheet_input_names ?? []).map((name) => <label key={name} className="block text-[11px]">{name}<input className="w-full border rounded px-1 py-0.5" value={row.sheet_inputs?.[name] ?? ''} onChange={(event) => {
          const value = Number(event.target.value)
          const inputs = { ...(row.sheet_inputs ?? {}), [name]: value }
          setPreviewRows((current) => current.map((item) => item.id === row.id ? { ...item, sheet_inputs: inputs, unit_price: null, line_total: 0, quote_status: 'RESOLVING' } : item))
          const rule = activeSheetRules.find((candidate) => candidate.id === row.selected_rule_id)
          if (rule && (row.sheet_input_names ?? []).every((inputName) => Number.isFinite(inputName === name ? value : inputs[inputName]))) void calculateSheetRowPrice(row.id, rule, inputs)
        }} /></label>),
        formatter: (_val, row) => (row.sheet_input_names ?? []).map((name) => `${name}: ${row.sheet_inputs?.[name] ?? ''}`).join(', '),
      },
      {
        key: 'color',
        label: 'Màu',
        width: 100,
        type: 'text',
        align: 'left',
        readOnly: true,
        sortable: true,
        filterable: true,
        formatter: (val) => String(val || ''),
      },
      {
        key: 'density',
        label: 'Định lượng',
        width: 90,
        type: 'text',
        align: 'left',
        readOnly: true,
        sortable: true,
        filterable: true,
        formatter: (val) => String(val || ''),
      },
      {
        key: 'uom',
        label: 'ĐVT',
        width: 60,
        type: 'text',
        align: 'center',
        readOnly: true,
        sortable: true,
      },
      {
        key: 'qty_gross',
        label: 'SL tính toán (BOM)',
        width: 120,
        type: 'number',
        align: 'right',
        readOnly: true,
        sortable: true,
        formatter: (val) => formatVnNumber(Number(val) || 0),
      },
      {
        key: 'qty_actual_buy',
        label: 'SL mua thực tế',
        width: 120,
        type: 'number',
        align: 'right',
        readOnly: false,
        sortable: true,
        formatter: (val) => formatVnNumber(Number(val) || 0),
      },
      {
        key: 'unit_price',
        label: 'Đơn giá (VNĐ)',
        width: 170,
        type: 'number',
        align: 'right',
        readOnly: true,
        sortable: true,
        renderCell: (val, row) => {
          const num = Number(val)
          return (
            <div className="flex flex-col items-end gap-1 w-full">
              <div className="flex items-center justify-end gap-1">
                {num && num > 0 ? (
                  <span className="font-mono text-slate-900">{formatVnNumber(num)} ₫</span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 font-medium text-amber-700 border border-amber-200 text-xs">
                    ⚠️ Chưa có giá
                  </span>
                )}
                {row.selected_rule_id ? (
                  <span className="text-[10px] text-emerald-600 font-semibold" title="Báo giá NCC">
                    [Báo giá]
                  </span>
                ) : (
                  <span className="text-[10px] text-slate-400" title="Nhập tay">
                    [Tay]
                  </span>
                )}
              </div>
              {(() => {
                const h1Key = getH1BucketKey(row.material_id, row.uom, row.material_spec)
                const sameCount = h1Key ? (sameMaterialCounts.get(h1Key) || 0) : 0
                if (sameCount > 1 && row.price_source === 'manual') {
                  return (
                    <div
                      data-testid={`synced-price-badge-${row.id}`}
                      className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 leading-tight text-right font-medium"
                      title={`Đơn giá tay được tự động đồng bộ cho ${sameCount} dòng cùng vật tư`}
                    >
                      Đồng bộ {sameCount} dòng
                    </div>
                  )
                }
                return null
              })()}
              {row.price_warning && (
                <div
                  className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 leading-tight text-right max-w-full truncate"
                  title={row.price_warning}
                >
                  {row.price_warning}
                </div>
              )}
            </div>
          )
        },
        formatter: (val) => {
          const num = Number(val)
          return num && num > 0 ? `${formatVnNumber(num)} ₫` : 'Chưa có giá'
        },
      },
      {
        key: 'line_total',
        label: 'Thành tiền (VNĐ)',
        width: 140,
        align: 'right',
        readOnly: true,
        sortable: true,
        type: 'currency',
        renderCell: (_val, row) => {
          const total = row.line_total || 0
          const isManual = row.price_source === 'manual'
          return (
            <div className="flex flex-col items-end justify-center py-0.5">
              <span className="font-mono font-medium text-slate-800">
                {formatVnNumber(total)} ₫
              </span>
              {isManual && total > 0 && (
                <span
                  data-testid={`provisional-badge-${row.id}`}
                  className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 mt-0.5 leading-tight font-medium"
                >
                  Tạm tính
                </span>
              )}
            </div>
          )
        },
        formatter: (_val, row) => {
          const total = row.line_total || 0
          return `${formatVnNumber(total)} ₫${row.price_source === 'manual' && total > 0 ? ' (Tạm tính)' : ''}`
        },
      },
      {
        key: 'resolution_status',
        label: 'Trạng thái',
        width: 160,
        align: 'center',
        readOnly: true,
        sortable: true,
        renderCell: (val, row) =>
          val === 'resolved' ? (
            <StatusBadge tone="success" label={row.manual_resolution ? 'Đã khớp (chọn tay)' : 'Đã khớp'} />
          ) : (
            <span title={row.resolution_notes || 'Vật tư chưa liên kết danh mục'}>
              <StatusBadge tone="danger" label="Chưa khớp — không vào PO" />
            </span>
          ),
      },
      {
        key: 'notes',
        label: 'Ghi chú',
        width: 180,
        type: 'text',
        align: 'left',
        readOnly: false,
        formatter: (val) => String(val || ''),
      },
      {
        key: 'action' as any,
        label: 'Thao tác',
        width: 85,
        align: 'center',
        readOnly: true,
        renderCell: (_val, row) =>
          isEditingRows ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-1.5 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => handleRemoveRow(row.id)}
              title="Xóa dòng này khỏi đơn PO"
            >
              Xóa
            </Button>
          ) : null,
      },
    ]
  }, [sameMaterialCounts, isEditingRows, bomSource, selectedProducts, activeSheetRules])

  // Cột "Thao tác" (nút Xóa) chỉ có khi đang Sửa; ngoài chế độ Sửa nó trống nên bỏ cột cho bảng gọn hơn.
  const visibleTableColumns = useMemo(
    () => tableColumns.filter((c) => (c.key as string) !== 'action' || isEditingRows),
    [tableColumns, isEditingRows],
  )

  const emptyRowFactory = (): BomPreviewTableRow => ({
    id: `temp-${Date.now()}`,
    product_id: '',
    product_code: '',
    material_id: null,
    material_code: null,
    material_name: '',
    spec: '',
    dimensions: '',
    density: '',
    bom_assembly: '',
    bom_packaging: '',
    uom: '',
    qty_gross: 0,
    qty_actual_buy: 0,
    unit_price: null,
    line_total: 0,
    selected_rule_id: null,
    price_source: 'none',
    manual_reason: '',
    price_warning: null,
    resolution_status: 'unresolved',
    resolution_notes: '',
    notes: '',
    source_bom_line_id: '',
    bom_source: bomSource,
  })

  return (
    <div className="bom-workspace flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
      <WorkspacePage
        className="bom-workspace-page flex-1"
        header={
          <>
            <div className="bom-workspace-header">
              <Breadcrumb
                items={[
                  { label: 'Trang chủ', to: '/' },
                  { label: 'Mua hàng', to: '/purchasing' },
                  { label: 'Tạo PO từ BOM' },
                ]}
              />
              <PageHeader
                title="Tạo đơn mua hàng từ BOM"
                subtitle="Chọn Nhà cung cấp, Loại hàng và nhiều mã hàng để tự động tính toán nhu cầu vật tư theo định mức BOM"
              />
            </div>
          </>
        }
        form={{
          content: loadingMasters ? (
            <div className="rounded-[var(--radius-md)] border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              Đang nạp dữ liệu Nhà cung cấp & Danh mục sản phẩm...
            </div>
          ) : (
            <div className={previewRows.length > 0 ? 'space-y-3' : 'space-y-6'}>
          {/* PHẦN 1: BƯỚC 1-2: CHỌN NCC & LOẠI HÀNG */}
          <section className={`rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white ${previewRows.length > 0 ? 'p-3' : 'p-5'} shadow-sm`}>
            <h2 className={`${previewRows.length > 0 ? 'mb-2' : 'mb-4'} flex items-center gap-2 text-sm font-semibold text-[var(--color-text)]`}>
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-accent)] text-xs text-white">
                1
              </span>
              Thông tin Nhà cung cấp & Loại hàng
            </h2>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Loại hàng (bom_source) */}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700">
                  Loại hàng (Nguồn BOM) <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-4 pt-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="bomSource"
                      value="foam"
                      checked={bomSource === 'foam'}
                      onChange={() => {
                        invalidateResolveBom()
                        setBomSource('foam')
                        setPreviewRows([])
                      }}
                      className="text-[var(--color-accent)] focus:ring-[var(--color-accent)]"
                    />
                    <span>Bao bì + Mút xốp</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="bomSource"
                      value="hardware"
                      checked={bomSource === 'hardware'}
                      onChange={() => {
                        invalidateResolveBom()
                        setBomSource('hardware')
                        setPreviewRows([])
                      }}
                      className="text-[var(--color-accent)] focus:ring-[var(--color-accent)]"
                    />
                    <span>Ngũ kim</span>
                  </label>
                </div>
              </div>

              {/* Ngày giao hàng dự kiến */}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700">
                  Ngày giao hàng dự kiến <span className="text-red-500">*</span>
                </label>
                <DateInput
                  value={expectedDeliveryDate}
                  onChange={(val) => {
                    invalidateResolveBom()
                    setExpectedDeliveryDate(val)
                    for (const row of previewRows) { const rule = activeSheetRules.find((candidate) => candidate.id === row.selected_rule_id); if (rule) void calculateSheetRowPrice(row.id, rule, row.sheet_inputs ?? {}) }
                  }}
                  className="w-full text-xs font-mono-num text-slate-800"
                />
              </div>
            </div>

            {/* Ghi chú về Gỗ */}
            <div className={`${previewRows.length > 0 ? 'mt-2 p-2' : 'mt-4 p-2.5'} flex items-start gap-2 rounded border border-blue-100 bg-blue-50/70 text-xs text-blue-900`}>
              <Layers className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
              <div>
                <strong>Lưu ý về nguồn Gỗ:</strong> Mặt hàng Gỗ nằm ngoài phạm vi tính toán tự động từ BOM theo quyết định kiến trúc hệ thống. Nếu cần tạo đơn mua Gỗ, vui lòng sử dụng luồng{' '}
                <Link to="/purchasing/po/new" className="font-semibold text-blue-700 underline hover:text-blue-900">
                  Tạo PO thủ công →
                </Link>
              </div>
            </div>

            {/* Ghi chú đơn hàng */}
            <div className={previewRows.length > 0 ? 'mt-2' : 'mt-3'}>
              <label className="mb-1 block text-xs font-medium text-slate-700">
                Ghi chú đơn mua hàng (tùy chọn)
              </label>
              <input
                type="text"
                value={poNote}
                onChange={(e) => setPoNote(e.target.value)}
                placeholder="Ví dụ: Đợt mua phục vụ đơn hàng xuất khẩu Lô 2..."
                className="w-full rounded-[var(--radius-sm)] border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
              />
            </div>
          </section>

          {/* PHẦN 2: BƯỚC 3: CHỌN NHIỀU MÃ HÀNG & THUỘC TÍNH */}
          <section className={`rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white ${previewRows.length > 0 ? 'p-3' : 'p-5'} shadow-sm`}>
            <div className={`${previewRows.length > 0 ? 'mb-2' : 'mb-4'} flex flex-wrap items-center justify-between gap-3`}>
              <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)]">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-accent)] text-xs text-white">
                  2
                </span>
                Danh sách mã hàng đặt sản xuất ({selectedProducts.length})
              </h2>

              {/* Form thêm sản phẩm */}
              <div className="flex items-center gap-2">
                <ProductSearchPicker<Product>
                  className="w-72"
                  search={searchProductsForPicker}
                  value={productToAdd}
                  onChange={setProductToAdd}
                />
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleAddProduct}
                  disabled={!productToAdd}
                >
                  <Plus className="h-3.5 w-3.5" /> Thêm mã hàng
                </Button>
              </div>
            </div>

            {selectedProducts.length === 0 ? (
              <div className="rounded border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
                Chưa có mã hàng nào được chọn. Gõ mã hàng ở ô phía trên, chọn mã trong danh sách rồi bấm <strong>+ Thêm mã hàng</strong>.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 font-semibold text-slate-700">
                      <th className="p-2.5 text-center w-12">STT</th>
                      <th className="p-2.5 w-56">Mã hàng / Tên sản phẩm</th>
                      <th className="p-2.5 w-32">SL đặt (sản phẩm) *</th>
                      {bomSource === 'foam' ? (
                        <th className="p-2.5 w-56">Cấu hình BOM (kết cấu - đóng gói) *</th>
                      ) : (
                        <th className="p-2.5 w-40">Kết cấu (assembly) *</th>
                      )}
                      {bomSource === 'hardware' && (
                        <>
                          <th className="p-2.5 w-32">Màu sắc (color)</th>
                          <th className="p-2.5 w-32">Loại gỗ (wood_type)</th>
                        </>
                      )}
                      <th className="p-2.5 w-44">Cấu hình chỉ định</th>
                      <th className="p-2.5 text-center w-16">Xóa</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {selectedProducts.map((p, idx) => (
                      <tr key={p.id} className="hover:bg-slate-50/70">
                        <td className="p-2.5 text-center text-slate-500">{idx + 1}</td>
                        <td className="p-2.5">
                          <div className="font-semibold text-slate-900">{p.product_code}</div>
                          <div className="text-[11px] text-slate-500 truncate max-w-xs">{p.product_name}</div>
                        </td>
                        <td className="p-2.5">
                          <input
                            type="number"
                            min="1"
                            value={p.order_qty || ''}
                            onChange={(e) =>
                              handleUpdateProduct(p.id, {
                                order_qty: parseProductOrderQty(e.target.value),
                              })
                            }
                            className="w-24 rounded border border-slate-300 px-2 py-1 text-right text-xs focus:border-[var(--color-accent)] focus:outline-none"
                          />
                        </td>
                        {bomSource === 'foam' ? (
                          (() => {
                            const st = foamChoices[p.product_id]
                            const choices = st?.choices ?? []
                            const chosen = findFoamChoice(choices, p.assembly_type, p.packaging_type)
                            const none = !!st && !st.loading && !st.error && choices.length === 0
                            return (
                              <td className="p-2.5">
                                {st?.error ? (
                                  <div className="text-[11px] text-red-600" title={st.error}>
                                    Lỗi tải cấu hình ({st.error})
                                  </div>
                                ) : none ? (
                                  <div className="text-[11px] font-medium text-red-600">
                                    Mã hàng chưa có BOM đóng gói
                                  </div>
                                ) : (
                                  <select
                                    aria-label={`Cấu hình BOM của ${p.product_code}`}
                                    value={chosen ? chosen.key : ''}
                                    disabled={!st || st.loading}
                                    onChange={(e) => {
                                      const picked = choices.find((c) => c.key === e.target.value)
                                      handleUpdateProduct(
                                        p.id,
                                        picked
                                          ? { assembly_type: picked.assembly, packaging_type: picked.packaging }
                                          : { assembly_type: '', packaging_type: undefined },
                                      )
                                    }}
                                    className={`w-full rounded border bg-white px-2 py-1 text-xs focus:border-[var(--color-accent)] focus:outline-none ${
                                      !chosen && st && !st.loading ? 'border-red-300' : 'border-slate-300'
                                    }`}
                                  >
                                    <option value="">
                                      {!st || st.loading ? 'Đang tải...' : '-- Chọn cấu hình --'}
                                    </option>
                                    {choices.map((c) => (
                                      <option key={c.key} value={foamChoiceKey(c.assembly, c.packaging)}>
                                        {c.label}
                                        {c.incomplete ? ' (đang cập nhật)' : ''}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </td>
                            )
                          })()
                        ) : (
                          <td className="p-2.5">
                            <select
                              value={p.assembly_type}
                              onChange={(e) =>
                                handleUpdateProduct(p.id, {
                                  assembly_type: e.target.value as StandardAssemblyType,
                                })
                              }
                              className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs focus:border-[var(--color-accent)] focus:outline-none"
                            >
                              {p.assembly_type === '' && <option value="">-- Chọn --</option>}
                              <option value="kd">{assemblyTypeLabel('kd')}</option>
                              <option value="assembled">{assemblyTypeLabel('assembled')}</option>
                            </select>
                          </td>
                        )}
                        {bomSource === 'hardware' && (() => {
                          const optState = productConfigOptions[p.product_id]
                          const isLoading = optState?.loading ?? false
                          const hasError = Boolean(optState?.error)
                          const colors = optState?.colors ?? []
                          const woods = optState?.woods ?? []
                          const noColors = !isLoading && !hasError && colors.length === 0
                          const noWoods = !isLoading && !hasError && woods.length === 0

                          return (
                            <>
                              <td className="p-2.5">
                                {hasError ? (
                                  <div className="text-[11px] text-red-600" title={optState?.error || ''}>
                                    Lỗi tải màu ({optState?.error})
                                  </div>
                                ) : (
                                  <select
                                    value={p.color || ''}
                                    disabled={isLoading || noColors}
                                    onChange={(e) => handleUpdateProduct(p.id, { color: e.target.value })}
                                    className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs focus:border-[var(--color-accent)] focus:outline-none disabled:bg-slate-100 disabled:text-slate-400"
                                    title={noColors ? 'Mã hàng không có cấu hình màu' : undefined}
                                  >
                                    <option value="">
                                      {isLoading
                                        ? 'Đang tải...'
                                        : noColors
                                          ? '— (Không có màu) —'
                                          : '— (Chưa chọn màu)'}
                                    </option>
                                    {colors.map((c) => (
                                      <option key={c} value={c}>
                                        {c}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </td>
                              <td className="p-2.5">
                                {hasError ? (
                                  <div className="text-[11px] text-red-600" title={optState?.error || ''}>
                                    Lỗi tải gỗ ({optState?.error})
                                  </div>
                                ) : (
                                  <select
                                    value={p.wood_type || ''}
                                    disabled={isLoading || noWoods}
                                    onChange={(e) => handleUpdateProduct(p.id, { wood_type: e.target.value })}
                                    className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs focus:border-[var(--color-accent)] focus:outline-none disabled:bg-slate-100 disabled:text-slate-400"
                                    title={noWoods ? 'Mã hàng không có cấu hình loại gỗ' : undefined}
                                  >
                                    <option value="">
                                      {isLoading
                                        ? 'Đang tải...'
                                        : noWoods
                                          ? '— (Không có loại gỗ) —'
                                          : '— (Chưa chọn loại gỗ)'}
                                    </option>
                                    {woods.map((w) => (
                                      <option key={w} value={w}>
                                        {w}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </td>
                            </>
                          )
                        })()}
                        <td className="p-2.5">
                          {p.override_foam_config_id || p.override_wood_config_id ? (
                            <div className="flex items-center gap-1.5">
                              <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[10px] text-amber-800">
                                {p.override_foam_config_id || p.override_wood_config_id}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleResetOverride(p.id)}
                                className="text-[11px] text-slate-400 hover:text-red-600"
                                title="Hủy cấu hình chỉ định"
                              >
                                ×
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-[11px]">Tự động theo thuộc tính</span>
                          )}
                        </td>
                        <td className="p-2.5 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveProduct(p.id)}
                            className="text-slate-400 hover:text-red-600 transition-colors"
                            title="Xóa mã hàng"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Error banner from validation or resolving */}
            {resolveError && (
              <div className="mt-4 flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div>
                  <span className="font-semibold">Lỗi BOM Adapter:</span> {resolveError}
                </div>
              </div>
            )}

            {/* Button: Lấy vật tư từ BOM */}
            <div className={`${previewRows.length > 0 ? 'mt-2' : 'mt-4'} flex justify-end`}>
              <Button
                variant="primary"
                onClick={handleResolveBom}
                disabled={isResolving || selectedProducts.length === 0}
              >
                {isResolving ? (
                  <>Đang tính toán BOM...</>
                ) : (
                  <>
                    <Layers className="h-4 w-4" /> Lấy vật tư từ BOM
                  </>
                )}
              </Button>
            </div>
          </section>

          </div>
          ),
          collapsible: true,
          collapsed: configurationCollapsed && previewRows.length > 0,
          summary: (
            <span>
              {bomSource === 'hardware' ? 'Ngũ kim' : 'Bao bì + Mút xốp'} · {selectedProducts.map((product) => [
                `${product.product_code} ×${product.order_qty}`,
                assemblyTypeLabel(product.assembly_type),
                product.color,
                product.wood_type,
                bomSource === 'foam' ? (product.packaging_type === 'ecom' ? 'Ecom' : 'Thường') : '',
              ].filter(Boolean).join(' ')).join(' · ')} · Giao {formatDateFull(expectedDeliveryDate)}
            </span>
          ),
          onToggle: setConfigurationCollapsed,
        }}
      >
          {!loadingMasters && previewRows.length > 0 && (
            <section className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white p-0 shadow-sm">
              <div className="bom-preview-heading flex flex-wrap items-center justify-between gap-3 px-3 pt-2 pb-1">
                <div>
                  <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text)]">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-accent)] text-xs text-white">
                      3
                    </span>
                    Bảng nhu cầu vật tư (Xem trước & Điều chỉnh SL mua thực tế & Đơn giá)
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Mỗi dòng hiển thị nhu cầu vật tư gắn với từng mã hàng (không gộp dòng). Cột <strong>SL mua thực tế</strong> và <strong>Đơn giá</strong> cho phép chỉnh sửa trực tiếp.
                  </p>
                </div>

                {/* Chế độ Sửa: chỉ khi bấm Sửa mới thay đổi được bảng */}
                <div className="flex items-center gap-2">
                  {isEditingRows ? (
                    <>
                      <Button size="sm" variant="primary" onClick={handleConfirmEditRows}>
                        ✓ Xác nhận
                      </Button>
                      <Button size="sm" variant="secondary" onClick={handleCancelEditRows}>
                        Hủy
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={handleStartEditRows}>
                      ✎ Sửa
                    </Button>
                  )}
                </div>

              </div>

              {/* Warning for manual price conflicts */}
              {priceConflicts.length > 0 && (
                <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                    <div className="flex-1">
                      <div className="font-semibold">
                        Cảnh báo xung đột đơn giá tay: Có {priceConflicts.length} vật tư đang có các mức giá nhập tay khác nhau
                      </div>
                      <div className="mt-1 space-y-1 text-red-800">
                        {priceConflicts.map((c) => (
                          <div key={c.h1Key}>
                            • Vật tư <strong>{c.material_name}</strong> (ĐVT: {c.uom}): Đang có các giá khác nhau{' '}
                            {c.prices.map((p) => formatVnNumber(p) + ' ₫').join(' vs ')}. Vui lòng sửa lại một dòng để hệ thống tự động đồng bộ thống nhất trước khi bấm Tạo PO.
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Warning for missing price */}
              {missingPriceCount > 0 && (
                <div className="bom-missing-price-warning mb-4 rounded border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
                    <div><strong>{missingPriceCount} dòng chưa có giá sẽ vào PO ở trạng thái Chưa có giá — điền giá hoặc xóa trên trang PO.</strong></div>
                  </div>
                </div>
              )}

              {/* ExcelEditableTable: tự vừa khít chiều ngang (chữ xuống dòng, không cuộn ngang) */}
              <style>{BOM_TABLE_AUTOFIT_CSS}</style>
              <div className="bom-autofit-table flex min-h-0 flex-1 border-y border-slate-200">
                <ExcelEditableTable<BomPreviewTableRow>
                  columns={visibleTableColumns}
                  data={previewRows}
                  onChange={handleTableChange}
                  emptyRowFactory={emptyRowFactory}
                  rowKey={(r) => r.id}
                  showActions={false}
                  readOnly={!isEditingRows}
                  resizableColumns
                  hiddenColumnsStorageKey="bom-from-bom-preview.hidden"
                  defaultHiddenColumnKeys={defaultHiddenBomColumns}
                  fill
                  rowHeight={34}
                  density="compact"
                  className="rounded-none border-0 shadow-none"
                />
              </div>

              {/* Create PO Error Banner */}
              {createPoError && (
                <div className="mt-4 flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                  <div>
                    <span className="font-semibold">Lỗi tạo đơn mua hàng:</span> {createPoError}
                  </div>
                </div>
              )}

              {/* Confirm & Create PO Action */}
              <div className="bom-workspace-footer sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-white px-3 py-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-slate-600">
                  <span className="rounded bg-slate-100 px-2 py-1">Tổng {previewRows.length} dòng</span>
                  <span className="rounded bg-emerald-50 px-2 py-1 text-emerald-700">Đã khớp {previewRows.length - unresolvedCount}</span>
                  <span className={`rounded px-2 py-1 ${unresolvedCount ? 'bg-red-50 text-red-700' : 'bg-slate-100'}`}>Chưa khớp: {unresolvedCount} (không vào PO)</span>
                  <span className={`rounded px-2 py-1 ${missingPriceCount ? 'bg-amber-100 text-amber-800' : 'bg-slate-100'}`}>Chưa có giá {missingPriceCount}</span>
                  {unresolvedCount > 0 && <>
                    <button type="button" className="text-red-700 underline disabled:opacity-50" disabled={!isEditingRows} title={isEditingRows ? undefined : 'Bấm Sửa để bỏ dòng chưa khớp'} onClick={() => setIsRemoveUnresolvedModalOpen(true)}>Bỏ {unresolvedCount} dòng chưa khớp</button>
                    <Link to="/purchasing/materials" className="text-blue-700 underline">Quản lý danh mục vật tư →</Link>
                  </>}
                  {createdSupplierPos.length > 0 && <div className="flex min-w-0 flex-wrap items-center gap-x-3 text-emerald-700">
                    <strong>Đã tạo {createdSupplierPos.length} PO · {unresolvedCount} dòng chưa khớp không đưa vào PO</strong>
                    {createdSupplierPos.map((po) => <Link key={po.id} to={`/purchasing/po/${po.id}`} className="underline" title={`${po.supplierName} · ${po.lineCount} dòng · ${formatVnNumber(po.total)} ₫`}>{po.code} · {po.supplierName} · {po.lineCount} dòng · {formatVnNumber(po.total)} ₫</Link>)}
                  </div>}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="text-slate-500">
                  {isEditingRows ? (
                    <span className="text-amber-700 font-medium">
                      ✎ Đang sửa bảng. Bấm Xác nhận hoặc Hủy trước khi tạo PO.
                    </span>
                  ) : priceConflicts.length > 0 ? (
                    <span className="text-red-600 font-medium">
                      ⚠️ Có {priceConflicts.length} vật tư bị xung đột đơn giá tay. Phải thống nhất giá trước khi bấm Tạo PO.
                    </span>
                  ) : (
                    <span className="text-emerald-700 font-medium">{pricedResolvedCount} dòng đã có giá</span>
                  )}
                  </div>

                <Button
                  variant="primary"
                  onClick={handleCreatePo}
                  disabled={
                    isCreatingPo ||
                    isEditingRows ||
                    previewRows.length === 0 ||
                    previewRows.some((row) => row.resolution_status === 'resolved' && !row.supplier_id) ||
                    previewRows.some((row) => row.resolution_status === 'resolved' && row.supplier_requires_confirmation)
                  }
                >
                  {isCreatingPo ? (
                    <>Đang tạo đơn PO & Snapshot...</>
                  ) : (
                    <>
                  Tạo PO theo NCC ({new Set(rowsEligibleForBomPurchaseOrders(previewRows).map((row) => row.supplier_id)).size} NCC) <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
              </div>
            </section>
          )}
      </WorkspacePage>

      {/* MODAL 1: Giải quyết Ambiguous Configuration */}
      {rulePickerRowId && (() => {
        const row = previewRows.find((item) => item.id === rulePickerRowId)
        if (!row?.supplier_id) return null
        return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"><div className="w-full max-w-5xl rounded-lg bg-white p-4 shadow-xl"><PoRulePickerModal supplierId={row.supplier_id} itemId={row.resolution_status === 'unresolved' ? null : row.material_id} initialSearch={row.resolution_status === 'unresolved' ? (row.bom_material_name || row.material_name) : ''} selectedRuleId={row.selected_rule_id} sheetOnly onClose={() => setRulePickerRowId(null)} onSelect={(rule) => {
          const active = activeSheetRules.find((candidate) => candidate.id === rule.id)
          const selectedRule: ActiveRuleOption = { ...(active ?? rule), supplier_id: row.supplier_id ?? undefined, supplier_name: row.supplier_name ?? undefined }
          const inputNames = (rule.input_names ?? []).filter((name) => name.trim() !== 'ĐV đơn giá')
          const inputs = Object.fromEntries(inputNames.flatMap((name) => {
            const mapped = row.purchase_inputs?.[name]
            const numeric = Number(mapped)
            return Number.isFinite(numeric) && mapped !== '' ? [[name, numeric]] : []
          }))
          const missing = inputNames.filter((name) => inputs[name] == null)
          setPreviewRows((current) => current.map((item) => {
            if (item.id !== row.id) return item
            const selected = item.resolution_status === 'unresolved'
              ? applySelectedRuleToUnresolvedRow(item, selectedRule)
              : { ...item, selected_rule_id: rule.id, rule_label: rule.supplier_item_name }
            return { ...selected, selected_rule_id: rule.id, rule_label: rule.supplier_item_name, sheet_input_names: inputNames, sheet_inputs: inputs, quote_status: missing.length ? 'MISSING_INPUT' : 'RESOLVING', price_warning: missing.length ? `Thiếu ${missing.join(', ')}` : null }
          }))
          setRulePickerRowId(null)
          if (!missing.length) void calculateSheetRowPrice(row.id, rule, inputs)
        }} /></div></div>
      })()}

      {ambiguousData?.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              Chọn cấu hình BOM cho mã hàng {ambiguousData.product_code}
            </h3>
            <p className="mt-1 text-xs text-slate-600">
              {ambiguousData.reason}. Vui lòng chọn 1 cấu hình cụ thể bên dưới để hệ thống tính toán chính xác:
            </p>

            <div className="mt-3 max-h-64 space-y-2 overflow-y-auto">
              {ambiguousData.candidates.map((cand, idx) => (
                <div
                  key={cand.id}
                  className="flex items-center justify-between rounded border border-slate-200 p-2.5 text-xs hover:border-[var(--color-accent)] hover:bg-slate-50"
                >
                  <div>
                    <div className="font-semibold text-slate-900">
                      Cấu hình #{idx + 1}: {cand.config_name || cand.variant_name || cand.id}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">
                      ID: <span className="font-mono">{cand.id}</span>
                      {cand.assembly_type && ` · Kết cấu: ${cand.assembly_type}`}
                      {cand.packaging_type && ` · Đóng gói: ${cand.packaging_type}`}
                      {cand.color && ` · Màu: ${cand.color}`}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleSelectAmbiguousCandidate(cand.id)}
                  >
                    Chọn cấu hình này
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setAmbiguousData(null)}
              >
                Đóng
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: Xác nhận bỏ dòng Unresolved */}
      {isRemoveUnresolvedModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              Xác nhận bỏ {unresolvedCount} dòng vật tư chưa khớp?
            </h3>
            <p className="mt-2 text-xs text-slate-600">
              Các dòng vật tư chưa khớp mã danh mục sẽ bị xóa khỏi danh sách preview này. Đơn mua hàng (PO) sẽ chỉ được tạo cho các vật tư đã khớp thành công.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsRemoveUnresolvedModalOpen(false)}
              >
                Hủy
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={handleRemoveAllUnresolved}
              >
                Xác nhận bỏ dòng chưa khớp
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
